import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxon3Plugin from "@fullcalendar/luxon3";

import EventModal from "./components/EventModal";
import { getAuthToken, getAuthTokenClaims } from "./api/authApi";
import { useEvents } from "./hooks/useEvents";
import SeriesActionModal from "./components/SeriesActionModal";
import RecurringScopeModal from "./components/RecurringScopeModal";
import CategoryManagerModal from "./components/CategoryManagerModal";
import NotificationSettingsModal from "./components/NotificationSettingsModal";
import { submitTimeEntry, updateTimeEntry, deleteTimeEntry } from "./api/timeEntryApi";
import { fetchOutstandingInvoices } from "./api/invoicesApi";
import { fetchCategories, createCategory, updateCategory, deleteCategory } from "./api/categoriesApi";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  fetchDueReminders,
  acknowledgeReminders,
  fetchDispatcherStatus,
  sendWhatsAppTestNotification,
} from "./api/notificationsApi";
import tippy, { hideAll } from "tippy.js";
import "tippy.js/dist/tippy.css";
import {
  SG_HOLIDAY_COUNTRY_CODE,
  UK_HOLIDAY_COUNTRY_CODE,
} from "./hooks/useHolidayEvents";
import { getRepeatValidationError } from "./utils/repeatValidation";
import "./Calendar.css";

const emptyEvent = {
  create_request_id: "",
  title: "",
  start: "",
  end: "",
  repeat_rule: "none",
  repeat_until: "",
  repeat_interval: 1,
  all_day: false,
  is_lesson: false,
  no_show: false,
  fb_time_entry_id: "",
  project_id: null,
  project_name: "",
  invoice_notes: "",
  internal_notes: "",
  location: "",
  category_id: "",
  category_name: "",
  category_color: "",
  reminder_enabled: false,
  reminder_config: "",
  attachments: [],
  series_id: "",
};

const newCreateRequestId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const getPerfNowMs = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const roundPerfMs = (value) => Math.round(Number(value) || 0);

const startPerfSpan = (name, meta = {}) => {
  const startedAt = getPerfNowMs();
  let previousStepAt = startedAt;
  console.info(`[perf] ${name} started`, meta);

  return {
    step(stepName, stepMeta = {}) {
      const now = getPerfNowMs();
      const elapsedMs = roundPerfMs(now - startedAt);
      const stepMs = roundPerfMs(now - previousStepAt);
      previousStepAt = now;
      console.info(`[perf] ${name} step:${stepName} +${stepMs}ms (${elapsedMs}ms total)`, {
        stepMs,
        elapsedMs,
        ...stepMeta,
      });
    },
    end(status = "ok", endMeta = {}) {
      const now = getPerfNowMs();
      const totalMs = roundPerfMs(now - startedAt);
      const sinceLastStepMs = roundPerfMs(now - previousStepAt);
      console.info(`[perf] ${name} ${status} +${sinceLastStepMs}ms (${totalMs}ms total)`, {
        sinceLastStepMs,
        totalMs,
        ...endMeta,
      });
    },
  };
};

const SIDEBAR_TIMEZONE_OPTIONS = [
  { label: "LDN", zone: "Europe/London", name: "London" },
  { label: "PAR", zone: "Europe/Paris", name: "Paris" },
  { label: "BER", zone: "Europe/Berlin", name: "Berlin" },
  { label: "MAD", zone: "Europe/Madrid", name: "Madrid" },
  { label: "ROM", zone: "Europe/Rome", name: "Rome" },
  { label: "NY", zone: "America/New_York", name: "New York" },
  { label: "CHI", zone: "America/Chicago", name: "Chicago" },
  { label: "LA", zone: "America/Los_Angeles", name: "Los Angeles" },
  { label: "TOR", zone: "America/Toronto", name: "Toronto" },
  { label: "MEX", zone: "America/Mexico_City", name: "Mexico City" },
  { label: "SP", zone: "America/Sao_Paulo", name: "Sao Paulo" },
  { label: "DXB", zone: "Asia/Dubai", name: "Dubai" },
  { label: "JHB", zone: "Africa/Johannesburg", name: "Johannesburg" },
  { label: "SG", zone: "Asia/Singapore", name: "Singapore" },
  { label: "HKG", zone: "Asia/Hong_Kong", name: "Hong Kong" },
  { label: "SHA", zone: "Asia/Shanghai", name: "Shanghai" },
  { label: "TOK", zone: "Asia/Tokyo", name: "Tokyo" },
  { label: "SEL", zone: "Asia/Seoul", name: "Seoul" },
  { label: "BKK", zone: "Asia/Bangkok", name: "Bangkok" },
  { label: "MUM", zone: "Asia/Kolkata", name: "Mumbai" },
  { label: "SYD", zone: "Australia/Sydney", name: "Sydney" },
  { label: "PER", zone: "Australia/Perth", name: "Perth" },
  { label: "AKL", zone: "Pacific/Auckland", name: "Auckland" },
];

const WEATHER_LOCATION_BY_TIMEZONE = {
  "Europe/London": { latitude: 51.5074, longitude: -0.1278, placeName: "London" },
  "Europe/Paris": { latitude: 48.8566, longitude: 2.3522, placeName: "Paris" },
  "Europe/Berlin": { latitude: 52.52, longitude: 13.405, placeName: "Berlin" },
  "Europe/Madrid": { latitude: 40.4168, longitude: -3.7038, placeName: "Madrid" },
  "Europe/Rome": { latitude: 41.9028, longitude: 12.4964, placeName: "Rome" },
  "America/New_York": { latitude: 40.7128, longitude: -74.006, placeName: "New York" },
  "America/Chicago": { latitude: 41.8781, longitude: -87.6298, placeName: "Chicago" },
  "America/Los_Angeles": { latitude: 34.0522, longitude: -118.2437, placeName: "Los Angeles" },
  "America/Toronto": { latitude: 43.6532, longitude: -79.3832, placeName: "Toronto" },
  "America/Mexico_City": { latitude: 19.4326, longitude: -99.1332, placeName: "Mexico City" },
  "America/Sao_Paulo": { latitude: -23.5505, longitude: -46.6333, placeName: "Sao Paulo" },
  "Asia/Dubai": { latitude: 25.2048, longitude: 55.2708, placeName: "Dubai" },
  "Africa/Johannesburg": { latitude: -26.2041, longitude: 28.0473, placeName: "Johannesburg" },
  "Asia/Singapore": { latitude: 1.3521, longitude: 103.8198, placeName: "Singapore" },
  "Asia/Hong_Kong": { latitude: 22.3193, longitude: 114.1694, placeName: "Hong Kong" },
  "Asia/Shanghai": { latitude: 31.2304, longitude: 121.4737, placeName: "Shanghai" },
  "Asia/Tokyo": { latitude: 35.6762, longitude: 139.6503, placeName: "Tokyo" },
  "Asia/Seoul": { latitude: 37.5665, longitude: 126.978, placeName: "Seoul" },
  "Asia/Bangkok": { latitude: 13.7563, longitude: 100.5018, placeName: "Bangkok" },
  "Asia/Kolkata": { latitude: 19.076, longitude: 72.8777, placeName: "Mumbai" },
  "Australia/Sydney": { latitude: -33.8688, longitude: 151.2093, placeName: "Sydney" },
  "Australia/Perth": { latitude: -31.9523, longitude: 115.8613, placeName: "Perth" },
  "Pacific/Auckland": { latitude: -36.8485, longitude: 174.7633, placeName: "Auckland" },
};

const UK_WEATHER_LOCATION = WEATHER_LOCATION_BY_TIMEZONE["Europe/London"];

const WEATHER_CODE_LABELS = {
  0: { label: "Clear", iconDay: "clear-day", iconNight: "clear-night", severity: "none" },
  1: { label: "Mostly clear", iconDay: "partly-cloudy-day", iconNight: "partly-cloudy-night", severity: "none" },
  2: { label: "Partly cloudy", iconDay: "partly-cloudy-day", iconNight: "partly-cloudy-night", severity: "none" },
  3: { label: "Cloudy", iconDay: "cloudy", iconNight: "cloudy", severity: "none" },
  45: { label: "Fog", iconDay: "fog", iconNight: "fog", severity: "watch" },
  48: { label: "Rime fog", iconDay: "fog", iconNight: "fog", severity: "watch" },
  51: { label: "Light drizzle", iconDay: "rain", iconNight: "rain", severity: "watch" },
  53: { label: "Drizzle", iconDay: "rain", iconNight: "rain", severity: "watch" },
  55: { label: "Heavy drizzle", iconDay: "rain", iconNight: "rain", severity: "alert" },
  56: { label: "Freezing drizzle", iconDay: "rain", iconNight: "rain", severity: "alert" },
  57: { label: "Freezing drizzle", iconDay: "rain", iconNight: "rain", severity: "alert" },
  61: { label: "Light rain", iconDay: "rain", iconNight: "rain", severity: "watch" },
  63: { label: "Rain", iconDay: "rain", iconNight: "rain", severity: "watch" },
  65: { label: "Heavy rain", iconDay: "rain", iconNight: "rain", severity: "alert" },
  66: { label: "Freezing rain", iconDay: "rain", iconNight: "rain", severity: "alert" },
  67: { label: "Freezing rain", iconDay: "rain", iconNight: "rain", severity: "alert" },
  71: { label: "Light snow", iconDay: "snow", iconNight: "snow", severity: "watch" },
  73: { label: "Snow", iconDay: "snow", iconNight: "snow", severity: "watch" },
  75: { label: "Heavy snow", iconDay: "snow", iconNight: "snow", severity: "alert" },
  77: { label: "Snow grains", iconDay: "snow", iconNight: "snow", severity: "watch" },
  80: { label: "Rain showers", iconDay: "rain", iconNight: "rain", severity: "watch" },
  81: { label: "Rain showers", iconDay: "rain", iconNight: "rain", severity: "alert" },
  82: { label: "Heavy showers", iconDay: "thunder", iconNight: "thunder", severity: "alert" },
  85: { label: "Snow showers", iconDay: "snow", iconNight: "snow", severity: "watch" },
  86: { label: "Heavy snow showers", iconDay: "snow", iconNight: "snow", severity: "alert" },
  95: { label: "Thunderstorm", iconDay: "thunder", iconNight: "thunder", severity: "alert" },
  96: { label: "Thunderstorm", iconDay: "thunder", iconNight: "thunder", severity: "alert" },
  99: { label: "Thunderstorm", iconDay: "thunder", iconNight: "thunder", severity: "alert" },
};

const DEFAULT_WEATHER_META = { label: "Weather", iconDay: "cloudy", iconNight: "cloudy", severity: "none" };

const getWeatherMeta = (weatherCode) => WEATHER_CODE_LABELS[weatherCode] || DEFAULT_WEATHER_META;

const getWeatherIconKey = (weatherCode, isDay) => {
  const meta = getWeatherMeta(weatherCode);
  return isDay ? meta.iconDay : meta.iconNight;
};

const WIND_DIRECTION_LABELS = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
];

const normalizeDegrees = (degrees) => {
  const value = Number(degrees);
  if (!Number.isFinite(value)) return null;
  return ((value % 360) + 360) % 360;
};

const toCompassDirection = (degrees) => {
  const normalized = normalizeDegrees(degrees);
  if (!Number.isFinite(normalized)) return "--";
  const index = Math.round(normalized / 22.5) % 16;
  return WIND_DIRECTION_LABELS[index];
};

const toWindFlowDegrees = (fromDegrees) => {
  const normalizedFrom = normalizeDegrees(fromDegrees);
  if (!Number.isFinite(normalizedFrom)) return null;
  return normalizeDegrees(normalizedFrom + 180);
};

const WEATHER_ALERT_THRESHOLDS = {
  default: { watchPrecip: 45, alertPrecip: 70, watchWind: 30, alertWind: 45 },
  swansea: { watchPrecip: 40, alertPrecip: 65, watchWind: 24, alertWind: 36 },
};

const getAlertLevel = ({ currentSeverity, maxPrecip, maxWind, hasThunder, cityId }) => {
  const thresholds = WEATHER_ALERT_THRESHOLDS[cityId] || WEATHER_ALERT_THRESHOLDS.default;
  if (
    hasThunder ||
    currentSeverity === "alert" ||
    maxPrecip >= thresholds.alertPrecip ||
    maxWind >= thresholds.alertWind
  ) {
    return "alert";
  }
  if (
    currentSeverity === "watch" ||
    maxPrecip >= thresholds.watchPrecip ||
    maxWind >= thresholds.watchWind
  ) {
    return "watch";
  }
  return "none";
};

function WeatherIcon({ iconKey, className = "" }) {
  const cls = className ? `calendar-weather-icon ${className}` : "calendar-weather-icon";

  if (iconKey === "clear-day") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="4.5" fill="#f59e0b" />
        <g stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round">
          <line x1="12" y1="2.2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="21.8" />
          <line x1="2.2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="21.8" y2="12" />
          <line x1="5" y1="5" x2="7" y2="7" />
          <line x1="17" y1="17" x2="19" y2="19" />
          <line x1="17" y1="7" x2="19" y2="5" />
          <line x1="5" y1="19" x2="7" y2="17" />
        </g>
      </svg>
    );
  }

  if (iconKey === "clear-night") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <path d="M16.7 4.5a7.9 7.9 0 1 0 2.8 15.2 8.6 8.6 0 1 1-2.8-15.2z" fill="#4c6a92" />
      </svg>
    );
  }

  if (iconKey === "partly-cloudy-day" || iconKey === "partly-cloudy-night") {
    const sunFill = iconKey === "partly-cloudy-day" ? "#f59e0b" : "#4c6a92";
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <circle cx="9" cy="9" r="3.8" fill={sunFill} />
        <path d="M8 18h8.4a3.4 3.4 0 0 0 0-6.8 4.7 4.7 0 0 0-9.1 1.4A3 3 0 0 0 8 18z" fill="#9fb8d4" />
      </svg>
    );
  }

  if (iconKey === "rain") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <path d="M7.5 15.5h9a3.4 3.4 0 0 0 0-6.8 4.8 4.8 0 0 0-9.3 1.5A2.9 2.9 0 0 0 7.5 15.5z" fill="#8fb0d3" />
        <g stroke="#3a82cc" strokeWidth="1.7" strokeLinecap="round">
          <line x1="9" y1="17.2" x2="8.2" y2="20" />
          <line x1="12.2" y1="17.2" x2="11.4" y2="20" />
          <line x1="15.4" y1="17.2" x2="14.6" y2="20" />
        </g>
      </svg>
    );
  }

  if (iconKey === "snow") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <path d="M7.5 14.8h9a3.4 3.4 0 0 0 0-6.8 4.8 4.8 0 0 0-9.3 1.5 2.9 2.9 0 0 0 .3 5.3z" fill="#9fb8d4" />
        <g stroke="#78a6d6" strokeWidth="1.3" strokeLinecap="round">
          <line x1="9.3" y1="17.5" x2="9.3" y2="20.5" />
          <line x1="7.8" y1="19" x2="10.8" y2="19" />
          <line x1="13.8" y1="17.5" x2="13.8" y2="20.5" />
          <line x1="12.3" y1="19" x2="15.3" y2="19" />
        </g>
      </svg>
    );
  }

  if (iconKey === "thunder") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <path d="M7.5 14.7h9a3.4 3.4 0 0 0 0-6.8 4.8 4.8 0 0 0-9.3 1.5 2.9 2.9 0 0 0 .3 5.3z" fill="#90accb" />
        <path d="M12 15.2l-1.8 4h1.9l-1.1 3.1 3.8-4.9h-2l1.4-2.2z" fill="#f59e0b" />
      </svg>
    );
  }

  if (iconKey === "fog") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
        <path d="M7.5 13.5h9a3.4 3.4 0 0 0 0-6.8 4.8 4.8 0 0 0-9.3 1.5 2.9 2.9 0 0 0 .3 5.3z" fill="#a8bdd4" />
        <g stroke="#7a94b2" strokeWidth="1.5" strokeLinecap="round">
          <line x1="6.5" y1="16.5" x2="17.5" y2="16.5" />
          <line x1="7.5" y1="19" x2="16.5" y2="19" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false">
      <path d="M7.5 16h9a3.4 3.4 0 0 0 0-6.8 4.8 4.8 0 0 0-9.3 1.5A2.9 2.9 0 0 0 7.5 16z" fill="#9fb8d4" />
    </svg>
  );
}

function WindDirectionIndicator({ degrees, className = "", title = "" }) {
  const normalized = normalizeDegrees(degrees);
  if (!Number.isFinite(normalized)) return null;
  const cls = className ? `calendar-weather-wind-indicator ${className}` : "calendar-weather-wind-indicator";
  return (
    <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" focusable="false" title={title}>
      <g transform={`rotate(${normalized} 12 12)`}>
        <path d="M2.8 12h10.1V8.1l8.2 3.9-8.2 3.9v-3.9H2.8z" fill="#1f3f62" stroke="#f3f8ff" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M4.1 12h8.1" stroke="#88a5c2" strokeWidth="1.1" strokeLinecap="round" />
      </g>
    </svg>
  );
}

const UK_WEATHER_STORAGE_KEY = "ukWeatherCity";
const UK_WEATHER_CITIES = [
  { id: "london", label: "London", shortLabel: "LDN", latitude: 51.5074, longitude: -0.1278 },
  { id: "manchester", label: "Manchester", shortLabel: "MAN", latitude: 53.4808, longitude: -2.2426 },
  { id: "birmingham", label: "Birmingham", shortLabel: "BHM", latitude: 52.4862, longitude: -1.8904 },
  { id: "leeds", label: "Leeds", shortLabel: "LDS", latitude: 53.8, longitude: -1.5491 },
  { id: "glasgow", label: "Glasgow", shortLabel: "GLW", latitude: 55.8642, longitude: -4.2518 },
  { id: "bristol", label: "Bristol", shortLabel: "BRS", latitude: 51.4545, longitude: -2.5879 },
  { id: "liverpool", label: "Liverpool", shortLabel: "LVP", latitude: 53.4084, longitude: -2.9916 },
  { id: "edinburgh", label: "Edinburgh", shortLabel: "EDI", latitude: 55.9533, longitude: -3.1883 },
  { id: "cardiff", label: "Cardiff", shortLabel: "CDF", latitude: 51.4816, longitude: -3.1791 },
  { id: "swansea", label: "Swansea", shortLabel: "SWA", latitude: 51.6214, longitude: -3.9436 },
  { id: "belfast", label: "Belfast", shortLabel: "BFS", latitude: 54.5973, longitude: -5.9301 },
];

const MINI_MONTH_WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const DATE_ONLY_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const categoryColorRegex = /^#[0-9A-Fa-f]{6}$/;
const whatsappE164Regex = /^\+[1-9]\d{7,14}$/;

const getInitialUkWeatherCityId = () => {
  if (typeof window === "undefined") return "london";
  const stored = localStorage.getItem(UK_WEATHER_STORAGE_KEY);
  if (stored && UK_WEATHER_CITIES.some((city) => city.id === stored)) {
    return stored;
  }
  return "london";
};

const normalizeWhatsAppNumber = (rawValue) => {
  let value = String(rawValue || "").trim();
  if (value.toLowerCase().startsWith("whatsapp:")) {
    value = value.slice("whatsapp:".length).trim();
  }

  // Keep '+' and digits only so pasted values like '+44 7788 750676' normalize cleanly.
  value = value.replace(/[()\-\s]/g, "");
  return value;
};

const hexToTint = (hex, blendWithWhite = 0.5) => {
  if (!categoryColorRegex.test(hex)) return "";
  const value = hex.slice(1);
  const baseR = parseInt(value.slice(0, 2), 16);
  const baseG = parseInt(value.slice(2, 4), 16);
  const baseB = parseInt(value.slice(4, 6), 16);
  const ratio = Math.min(1, Math.max(0, blendWithWhite));
  const r = Math.round(baseR * (1 - ratio) + 255 * ratio);
  const g = Math.round(baseG * (1 - ratio) + 255 * ratio);
  const b = Math.round(baseB * (1 - ratio) + 255 * ratio);
  return `rgb(${r}, ${g}, ${b})`;
};

function Calendar({
  holidayEvents = [],
  holidayError = "",
  showUkHolidays = true,
  showSingaporeHolidays = false,
  timeFormat = "12h",
  onTimeFormatChange,
  onToggleUkHolidays,
  onToggleSingaporeHolidays,
  onVisibleRangeChange,
  desktopAgendaOpen = false,
  onToggleDesktopAgenda = null,
  initialCalendarView = "dayGridMonth",
  initialCalendarDate = null,
  onCalendarStateChange = null,
}) {
  const { events, createSilently, update, updateSilently, remove, removeSeries, removeSeriesFrom, reload } = useEvents();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [seriesModalInfo, setSeriesModalInfo] = useState(null);
  const [recurringScopeModal, setRecurringScopeModal] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [secondaryTimezone, setSecondaryTimezone] = useState("Europe/London");
  const [now, setNow] = useState(new Date());
  const [miniMonthsCollapsed, setMiniMonthsCollapsed] = useState(
    () => localStorage.getItem("miniMonthsCollapsed") === "true"
  );
  const [miniMonthOffset, setMiniMonthOffset] = useState(0);
  const [miniMonthHoverDay, setMiniMonthHoverDay] = useState(null);
  const [miniMonthContextMenu, setMiniMonthContextMenu] = useState(null);
  const miniMonthHoverTimerRef = useRef(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [showInvoiceTable, setShowInvoiceTable] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const [invoiceGeneratedAt, setInvoiceGeneratedAt] = useState("");
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    browser_enabled: false,
    quiet_hours_enabled: false,
    quiet_start: "22:00",
    quiet_end: "07:00",
    timezone: "Europe/London",
    poll_interval_seconds: 30,
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [whatsappTestSending, setWhatsappTestSending] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [notificationSaveMessage, setNotificationSaveMessage] = useState("");
  const [browserTestMessage, setBrowserTestMessage] = useState("");
  const [whatsappTestMessage, setWhatsappTestMessage] = useState("");
  const [savedNotificationSettings, setSavedNotificationSettings] = useState(null);
  const [dispatcherStatusOpen, setDispatcherStatusOpen] = useState(false);
  const [dispatcherStatusLoading, setDispatcherStatusLoading] = useState(false);
  const [dispatcherStatusError, setDispatcherStatusError] = useState("");
  const [dispatcherStatus, setDispatcherStatus] = useState(null);
  const [browserPermission, setBrowserPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported"
  );
  const [ukWeatherCityId, setUkWeatherCityId] = useState(getInitialUkWeatherCityId);
  const [weatherPopoverOpen, setWeatherPopoverOpen] = useState(false);
  const calendarUrlHint = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href.replace(/^https?:\/\//i, "");
  }, []);
  const [weatherSummary, setWeatherSummary] = useState({
    status: "idle",
    temperatureText: "--",
    windText: "--",
    windFromText: "--",
    windFlowDirectionDegrees: null,
    currentIcon: "cloudy",
    subtitle: "",
    highLow: "",
    nextRiskIcon: "cloudy",
    nextRiskText: "No near-term risk",
    alertLevel: "none",
    alertText: "No active alerts",
    forecastRows: [],
  });
  const [activeView, setActiveView] = useState({
    type: "dayGridMonth",
    start: null,
    end: null,
    periodStart: null,
    periodEnd: null,
  });

  useEffect(() => {
    if (typeof onVisibleRangeChange !== "function") return;
    const startDate = activeView.periodStart || activeView.start || new Date();
    const endDate = activeView.periodEnd || activeView.end || startDate;
    onVisibleRangeChange(startDate, endDate);
  }, [activeView.end, activeView.periodEnd, activeView.periodStart, activeView.start, onVisibleRangeChange]);

  useEffect(() => {
    if (!headerMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (!headerMenuRef.current) return;
      if (!headerMenuRef.current.contains(event.target)) {
        setHeaderMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [headerMenuOpen]);

  const secondaryTimezoneOption =
    SIDEBAR_TIMEZONE_OPTIONS.find((tz) => tz.zone === secondaryTimezone) ||
    SIDEBAR_TIMEZONE_OPTIONS[0];

  const selectedUkWeatherCity = useMemo(
    () => UK_WEATHER_CITIES.find((city) => city.id === ukWeatherCityId) || UK_WEATHER_CITIES[0],
    [ukWeatherCityId]
  );

  const sortedTimezoneOptions = useMemo(
    () => [...SIDEBAR_TIMEZONE_OPTIONS].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const sortedUkWeatherCities = useMemo(
    () => [...UK_WEATHER_CITIES].sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const weatherLocation = selectedUkWeatherCity || UK_WEATHER_LOCATION;

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(UK_WEATHER_STORAGE_KEY, ukWeatherCityId);
  }, [ukWeatherCityId]);

  useEffect(() => {
    const controller = new AbortController();
    const location = weatherLocation;

    if (!location) {
      setWeatherSummary({
        status: "error",
        temperatureText: "--",
        windText: "--",
        windFromText: "--",
        windFlowDirectionDegrees: null,
        currentIcon: "cloudy",
        subtitle: "UK",
        highLow: "",
        nextRiskIcon: "cloudy",
        nextRiskText: "No near-term risk",
        alertLevel: "none",
        alertText: "No active alerts",
        forecastRows: [],
      });
      return undefined;
    }

    setWeatherSummary({
      status: "loading",
      temperatureText: "--",
      windText: "--",
      windFromText: "--",
      windFlowDirectionDegrees: null,
      currentIcon: "cloudy",
      subtitle: `Loading for ${location.label}`,
      highLow: "",
      nextRiskIcon: "cloudy",
      nextRiskText: "No near-term risk",
      alertLevel: "none",
      alertText: "No active alerts",
      forecastRows: [],
    });

    const loadWeather = async () => {
      try {
        const url = new URL("https://api.open-meteo.com/v1/forecast");
        url.searchParams.set("latitude", String(location.latitude));
        url.searchParams.set("longitude", String(location.longitude));
        url.searchParams.set("current", "temperature_2m,weather_code,windspeed_10m,winddirection_10m,is_day");
        url.searchParams.set("hourly", "temperature_2m,precipitation_probability,weather_code,windspeed_10m,winddirection_10m,is_day");
        url.searchParams.set("daily", "temperature_2m_max,temperature_2m_min");
        url.searchParams.set("temperature_unit", "celsius");
        url.searchParams.set("forecast_days", "2");
        url.searchParams.set("timezone", "Europe/London");

        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(`Weather lookup failed (${response.status})`);
        const data = await response.json();
        const current = data?.current || {};
        const weatherCode = Number(current.weather_code);
        const currentIsDay = Number(current.is_day) === 1;
        const weatherInfo = getWeatherMeta(weatherCode);
        const currentIcon = getWeatherIconKey(weatherCode, currentIsDay);
        const temperature = Number(current.temperature_2m);
        const currentWind = Number(current.windspeed_10m);
        const currentWindFromDegrees = normalizeDegrees(current.winddirection_10m);
        const currentWindFlowDirectionDegrees = toWindFlowDegrees(current.winddirection_10m);
        const currentWindFromText = toCompassDirection(current.winddirection_10m);
        const temperatureText = Number.isFinite(temperature) ? `${Math.round(temperature)} C` : "--";
        const windText = Number.isFinite(currentWind) ? `${Math.round(currentWind)} km/h` : "--";

        const dailyMax = Number(data?.daily?.temperature_2m_max?.[0]);
        const dailyMin = Number(data?.daily?.temperature_2m_min?.[0]);
        const highLow = Number.isFinite(dailyMax) && Number.isFinite(dailyMin)
          ? `H ${Math.round(dailyMax)}°  L ${Math.round(dailyMin)}°`
          : "";

        const hourlyTimes = Array.isArray(data?.hourly?.time) ? data.hourly.time : [];
        const hourlyTemps = Array.isArray(data?.hourly?.temperature_2m) ? data.hourly.temperature_2m : [];
        const hourlyPrecip = Array.isArray(data?.hourly?.precipitation_probability)
          ? data.hourly.precipitation_probability
          : [];
        const hourlyCodes = Array.isArray(data?.hourly?.weather_code) ? data.hourly.weather_code : [];
        const hourlyWinds = Array.isArray(data?.hourly?.windspeed_10m) ? data.hourly.windspeed_10m : [];
        const hourlyWindDirections = Array.isArray(data?.hourly?.winddirection_10m) ? data.hourly.winddirection_10m : [];
        const hourlyIsDay = Array.isArray(data?.hourly?.is_day) ? data.hourly.is_day : [];
        const nowUk = DateTime.now().setZone("Europe/London");

        const forecastRows = [];
        let nextRiskRow = null;
        let maxPrecip = 0;
        let maxWind = 0;
        let hasThunder = false;
        for (let i = 0; i < hourlyTimes.length; i += 1) {
          const slotTime = DateTime.fromISO(hourlyTimes[i], { zone: "Europe/London" });
          if (!slotTime.isValid || slotTime < nowUk) continue;

          const slotTemp = Number(hourlyTemps[i]);
          const slotCode = Number(hourlyCodes[i]);
          const slotPrecip = Number(hourlyPrecip[i]);
          const slotWind = Number(hourlyWinds[i]);
          const slotWindFromDegrees = normalizeDegrees(hourlyWindDirections[i]);
          const slotWindFlowDirectionDegrees = toWindFlowDegrees(hourlyWindDirections[i]);
          const slotWindFromText = toCompassDirection(hourlyWindDirections[i]);
          const slotWindToText = toCompassDirection(slotWindFlowDirectionDegrees);
          const slotDayFlag = Number(hourlyIsDay[i]) === 1;
          const slotInfo = getWeatherMeta(slotCode);
          const slotIcon = getWeatherIconKey(slotCode, slotDayFlag);

          if (Number.isFinite(slotPrecip)) {
            maxPrecip = Math.max(maxPrecip, slotPrecip);
          }
          if (Number.isFinite(slotWind)) {
            maxWind = Math.max(maxWind, slotWind);
          }
          if (slotCode === 95 || slotCode === 96 || slotCode === 99) {
            hasThunder = true;
          }

          forecastRows.push({
            timeLabel: slotTime.toFormat(timeFormat === "24h" ? "HH:mm" : "h:mm a"),
            icon: slotIcon,
            tempLabel: Number.isFinite(slotTemp) ? `${Math.round(slotTemp)}°` : "--",
            rainLabel: Number.isFinite(slotPrecip) ? `${Math.round(slotPrecip)}% rain` : "--",
            windLabel: Number.isFinite(slotWind) ? `${Math.round(slotWind)} km/h` : "--",
            windFromText: slotWindFromText,
            windToText: slotWindToText,
            windFlowDirectionDegrees: slotWindFlowDirectionDegrees,
            windFromDegrees: slotWindFromDegrees,
            riskScore:
              (Number.isFinite(slotPrecip) ? slotPrecip : 0) +
              (slotInfo.severity === "alert" ? 80 : slotInfo.severity === "watch" ? 35 : 0),
            riskText:
              (Number.isFinite(slotPrecip) && slotPrecip >= 65) || slotInfo.severity === "alert"
                ? "High risk"
                : (Number.isFinite(slotPrecip) && slotPrecip >= 40) || slotInfo.severity === "watch"
                  ? "Watch"
                  : "Low risk",
          });

          if (forecastRows.length >= 6) break;
        }

        if (forecastRows.length > 0) {
          nextRiskRow = forecastRows.reduce((highest, row) => {
            if (!highest) return row;
            return row.riskScore > highest.riskScore ? row : highest;
          }, null);
        }

        const alertLevel = getAlertLevel({
          currentSeverity: weatherInfo.severity,
          maxPrecip,
          maxWind,
          hasThunder,
          cityId: selectedUkWeatherCity.id,
        });

        const alertText =
          alertLevel === "alert"
            ? `Alert: heavy weather risk (${Math.round(maxPrecip)}% rain peak, ${Math.round(maxWind)} km/h wind)`
            : alertLevel === "watch"
              ? `Watch: unsettled weather possible (${Math.round(maxPrecip)}% rain peak)`
              : "No active weather alerts";

        setWeatherSummary({
          status: "ready",
          temperatureText,
          windText,
          windFromText: currentWindFromText,
          windFlowDirectionDegrees: currentWindFlowDirectionDegrees,
          windFromDegrees: currentWindFromDegrees,
          currentIcon,
          subtitle: `${weatherInfo.label} in ${location.label}`,
          highLow,
          nextRiskIcon: nextRiskRow?.icon || currentIcon,
          nextRiskText: nextRiskRow ? `${nextRiskRow.riskText} around ${nextRiskRow.timeLabel}` : "No near-term risk",
          alertLevel,
          alertText,
          forecastRows,
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
        setWeatherSummary({
          status: "error",
          temperatureText: "--",
          windText: "--",
          windFromText: "--",
          windFlowDirectionDegrees: null,
          currentIcon: "cloudy",
          subtitle: location.label,
          highLow: "",
          nextRiskIcon: "cloudy",
          nextRiskText: "No near-term risk",
          alertLevel: "none",
          alertText: "No active alerts",
          forecastRows: [],
        });
      }
    };

    loadWeather();

    return () => controller.abort();
  }, [weatherLocation, selectedUkWeatherCity.id, timeFormat]);

  useEffect(() => {
    if (!weatherPopoverOpen) return undefined;

    const onPointerDown = (event) => {
      if (!weatherPopoverRef.current) return;
      if (!weatherPopoverRef.current.contains(event.target)) {
        setWeatherPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [weatherPopoverOpen]);

  const sidebarTimezones = [
    { label: secondaryTimezoneOption.label, zone: secondaryTimezoneOption.zone },
    { label: "UK", zone: "Europe/London" },
  ];

  const calendarRef = useRef(null);
  const headerMenuRef = useRef(null);
  const weatherPopoverRef = useRef(null);
  const recurringScopeResolverRef = useRef(null);
  const timeGridScrollPendingRef = useRef(false);
  const timeGridScrollTimerRef = useRef(null);
  const pendingUpdatedEventFocusRef = useRef(null);
  const suppressNextTimeGridScrollRef = useRef(false);
  const mountedEventElsRef = useRef(new Map());
  const previousViewTypeRef = useRef(String(initialCalendarView || "dayGridMonth"));
  const monthToWeekRedirectRef = useRef(false);
  const monthDateClickToDayRef = useRef(false);
  const editReturnContextRef = useRef({
    viewType: "",
    anchorDate: "",
  });

  const authClaims = useMemo(() => getAuthTokenClaims(), []);
  const isAdminUser = Boolean(authClaims?.is_admin);
  const hasUnsavedWhatsAppSettings = useMemo(() => {
    if (!savedNotificationSettings) return false;

    const currentEnabled = notificationSettings.whatsapp_enabled === true;
    const savedEnabled = savedNotificationSettings.whatsapp_enabled === true;
    const currentNumber = String(notificationSettings.whatsapp_number || "").trim();
    const savedNumber = String(savedNotificationSettings.whatsapp_number || "").trim();
    return currentEnabled !== savedEnabled || currentNumber !== savedNumber;
  }, [
    notificationSettings.whatsapp_enabled,
    notificationSettings.whatsapp_number,
    savedNotificationSettings,
  ]);

  const requestRecurringScope = useCallback((actionLabel) => {
    return new Promise((resolve) => {
      recurringScopeResolverRef.current = resolve;
      setRecurringScopeModal({ actionLabel });
    });
  }, []);

  const restoreViewAndFocusUpdatedEvent = useCallback((eventId, startValue) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi || !eventId) return;

    const { viewType, anchorDate } = editReturnContextRef.current || {};
    const targetView = viewType || calendarApi.view?.type || "";
    const shouldFocusEvent = targetView === "timeGridDay" || targetView === "timeGridWeek";
    if (!shouldFocusEvent) return;

    pendingUpdatedEventFocusRef.current = {
      eventId: String(eventId),
      startValue: String(startValue || anchorDate || "").slice(0, 10),
      viewType: targetView,
    };
    suppressNextTimeGridScrollRef.current = true;

    const targetDate = pendingUpdatedEventFocusRef.current.startValue;
    if (targetDate) {
      calendarApi.changeView(targetView, targetDate);
    }
  }, []);

  const focusPendingUpdatedEvent = useCallback((eventId, eventEl, viewType) => {
    const pending = pendingUpdatedEventFocusRef.current;
    if (!pending || String(pending.eventId) !== String(eventId)) return;
    if (pending.viewType && pending.viewType !== viewType) return;

    pendingUpdatedEventFocusRef.current = null;
    window.requestAnimationFrame(() => {
      if (!eventEl || typeof eventEl.scrollIntoView !== "function") return;
      eventEl.scrollIntoView({ block: "center", inline: "nearest" });
      if (eventEl._tippy && typeof eventEl._tippy.hide === "function") {
        eventEl._tippy.hide();
      }
    });
  }, []);

  const resolveRecurringScope = useCallback((value) => {
    const resolve = recurringScopeResolverRef.current;
    recurringScopeResolverRef.current = null;
    setRecurringScopeModal(null);
    if (resolve) {
      resolve(value);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (recurringScopeResolverRef.current) {
        recurringScopeResolverRef.current(null);
        recurringScopeResolverRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Manually paint the now-indicator line since we disabled nowIndicator to prevent
  // FullCalendar from auto-scrolling to current time on view entry.
  useEffect(() => {
    const paintNowLine = () => {
      const calendarEl =
        calendarRef.current?.getApi()?.el ||
        document.querySelector(".calendar-card .fc") ||
        document.querySelector(".fc");
      if (!calendarEl) return;

      // Only show in time-grid views.
      const viewType = calendarRef.current?.getApi()?.view?.type || "";
      if (!viewType.startsWith("timeGrid")) {
        calendarEl.querySelectorAll(".fc-now-indicator-manual").forEach((el) => el.remove());
        return;
      }

      const slotContainer = calendarEl.querySelector(".fc-timegrid-slots");
      if (!slotContainer) return;

      const n = now;
      const minutesFromMidnight = n.getHours() * 60 + n.getMinutes();
      const totalMinutes = 24 * 60;
      const pct = minutesFromMidnight / totalMinutes;
      const totalHeight = slotContainer.scrollHeight || slotContainer.offsetHeight;
      const top = Math.round(pct * totalHeight);

      let line = calendarEl.querySelector(".fc-now-indicator-manual");
      if (!line) {
        line = document.createElement("div");
        line.className = "fc-now-indicator fc-now-indicator-manual";
        line.style.position = "absolute";
        line.style.left = "0";
        line.style.right = "0";
        line.style.zIndex = "9";
        line.style.pointerEvents = "none";
        slotContainer.style.position = slotContainer.style.position || "relative";
        slotContainer.appendChild(line);
      }
      line.style.top = `${top}px`;
    };

    paintNowLine();
  }, [now, activeView]);

  useEffect(() => {
    return () => {
      clearTimeout(miniMonthHoverTimerRef.current);
      clearTimeout(timeGridScrollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const loadInitialNotificationSettings = async () => {
      try {
        const next = await fetchNotificationSettings();
        setNotificationSettings(next);
        setSavedNotificationSettings(next);
      } catch {
        // Keep defaults if settings are unavailable.
      }
    };

    loadInitialNotificationSettings();
  }, []);

  useEffect(() => {
    if (!notificationModalOpen) return;

    const loadNotificationSettings = async () => {
      setNotificationLoading(true);
      setNotificationError("");
      try {
        const next = await fetchNotificationSettings();
        setNotificationSettings(next);
        setSavedNotificationSettings(next);
      } catch (err) {
        setNotificationError(err.message || "Failed to load notification settings");
      } finally {
        setNotificationLoading(false);
      }
    };

    loadNotificationSettings();
  }, [notificationModalOpen]);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return undefined;
    if (Notification.permission !== "granted") return undefined;
    if (!notificationSettings.browser_enabled) return undefined;

    const intervalMs = Math.max(15000, Number(notificationSettings.poll_interval_seconds || 30) * 1000);
    let stopped = false;

    const pollDueReminders = async () => {
      if (stopped) return;

      try {
        const result = await fetchDueReminders(2);
        const reminders = Array.isArray(result?.reminders) ? result.reminders : [];
        if (!reminders.length) return;

        const acknowledged = [];
        reminders.forEach((reminder) => {
          const title = String(reminder.title || "Reminder");
          const body = String(reminder.body || "You have a scheduled reminder");
          new Notification(title, {
            body,
            tag: reminder.delivery_id,
          });
          acknowledged.push(reminder.delivery_id);
        });

        if (acknowledged.length) {
          await acknowledgeReminders(acknowledged);
        }
      } catch {
        // Silent fail to avoid user disruption when network is flaky.
      }
    };

    pollDueReminders();
    const interval = setInterval(pollDueReminders, intervalMs);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [notificationSettings.browser_enabled, notificationSettings.poll_interval_seconds]);

  const handleNotificationSettingsChange = useCallback((field, value) => {
    setNotificationSaveMessage("");
    setBrowserTestMessage("");
    setWhatsappTestMessage("");
    if (field === "whatsapp_number") {
      setNotificationSettings((current) => ({
        ...current,
        [field]: normalizeWhatsAppNumber(value),
      }));
      return;
    }
    setNotificationSettings((current) => ({ ...current, [field]: value }));
  }, []);

  const handleNotificationSettingsSave = useCallback(async () => {
    setNotificationSaving(true);
    setNotificationError("");
    setNotificationSaveMessage("");
    setBrowserTestMessage("");
    setWhatsappTestMessage("");

    const sanitized = {
      browser_enabled: notificationSettings.browser_enabled === true,
      email_enabled: notificationSettings.email_enabled === true,
      email_address: String(notificationSettings.email_address || "").trim(),
      whatsapp_enabled: notificationSettings.whatsapp_enabled === true,
      whatsapp_number: normalizeWhatsAppNumber(notificationSettings.whatsapp_number),
      quiet_hours_enabled: notificationSettings.quiet_hours_enabled === true,
      quiet_start: String(notificationSettings.quiet_start || "22:00"),
      quiet_end: String(notificationSettings.quiet_end || "07:00"),
      timezone: String(notificationSettings.timezone || "Europe/London").trim() || "Europe/London",
      poll_interval_seconds: Math.max(
        15,
        Math.min(300, Number.isFinite(Number(notificationSettings.poll_interval_seconds))
          ? Number(notificationSettings.poll_interval_seconds)
          : 30)
      ),
    };

    try {
      await updateNotificationSettings(sanitized);
      const refreshed = await fetchNotificationSettings();
      setNotificationSettings(refreshed);
      setSavedNotificationSettings(refreshed);
      setNotificationSaveMessage("Settings saved.");
    } catch (err) {
      setNotificationError(err.message || "Failed to save notification settings");
    } finally {
      setNotificationSaving(false);
    }
  }, [notificationSettings]);

  const handleNotificationPermissionRequest = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setBrowserPermission("unsupported");
      return;
    }

    const result = await Notification.requestPermission();
    setBrowserPermission(result);
  }, []);

  const handleTestNotification = useCallback(() => {
    setNotificationError("");
    setNotificationSaveMessage("");
    setWhatsappTestMessage("");

    if (typeof window === "undefined" || !("Notification" in window)) {
      setNotificationError("Browser notifications are not supported on this device.");
      return;
    }

    if (Notification.permission !== "granted") {
      setNotificationError("Enable browser permission first.");
      return;
    }

    new Notification("Reminder test", {
      body: "Browser reminders are enabled.",
      tag: `notification-test-${Date.now()}`,
      renotify: true,
    });
    setBrowserTestMessage("Browser test notification sent.");
  }, []);

  const handleTestWhatsApp = useCallback(async () => {
    setNotificationError("");
    setNotificationSaveMessage("");
    setBrowserTestMessage("");
    setWhatsappTestMessage("");

    if (!notificationSettings.whatsapp_enabled) {
      setNotificationError("Enable WhatsApp notifications first.");
      return;
    }

    const whatsappNumber = normalizeWhatsAppNumber(notificationSettings.whatsapp_number);
    if (!whatsappNumber) {
      setNotificationError("Enter a WhatsApp number before sending a test.");
      return;
    }

    if (!whatsappE164Regex.test(whatsappNumber)) {
      setNotificationError("WhatsApp number must be E.164 format, for example +447700900123.");
      return;
    }

    setWhatsappTestSending(true);
    try {
      const result = await sendWhatsAppTestNotification({ to_number: whatsappNumber });
      const message = String(result?.message || "WhatsApp test message queued successfully.");
      const destination = String(result?.to || whatsappNumber || "").trim();
      const sid = result?.message_sid ? ` (Message SID: ${result.message_sid})` : "";
      const destinationText = destination ? ` Destination: ${destination}.` : "";
      setWhatsappTestMessage(`${message}${sid}${destinationText}`);
    } catch (err) {
      setNotificationError(err.message || "Failed to send WhatsApp test notification");
    } finally {
      setWhatsappTestSending(false);
    }
  }, [
    notificationSettings.whatsapp_enabled,
    notificationSettings.whatsapp_number,
  ]);

  const formatStatusTimestamp = useCallback((isoValue) => {
    if (!isoValue) return "Not recorded";
    const parsed = DateTime.fromISO(String(isoValue));
    if (!parsed.isValid) return String(isoValue);
    return parsed.toLocal().toFormat("dd LLL yyyy, HH:mm:ss");
  }, []);

  const refreshDispatcherStatus = useCallback(async () => {
    setDispatcherStatusLoading(true);
    setDispatcherStatusError("");
    try {
      const payload = await fetchDispatcherStatus();
      setDispatcherStatus(payload);
    } catch (err) {
      setDispatcherStatusError(err.message || "Unable to load dispatcher status");
    } finally {
      setDispatcherStatusLoading(false);
    }
  }, []);

  const handleAdminToolsToggle = useCallback(async () => {
    setDispatcherStatusOpen((current) => {
      const next = !current;
      if (next && !dispatcherStatus && !dispatcherStatusLoading) {
        refreshDispatcherStatus();
      }
      return next;
    });
  }, [dispatcherStatus, dispatcherStatusLoading, refreshDispatcherStatus]);

  useEffect(() => {
    if (!miniMonthContextMenu) return undefined;

    const handleGlobalClose = () => {
      setMiniMonthContextMenu(null);
    };

    const handleEscapeClose = (event) => {
      if (event.key === "Escape") {
        setMiniMonthContextMenu(null);
      }
    };

    window.addEventListener("click", handleGlobalClose);
    window.addEventListener("scroll", handleGlobalClose, true);
    window.addEventListener("keydown", handleEscapeClose);

    return () => {
      window.removeEventListener("click", handleGlobalClose);
      window.removeEventListener("scroll", handleGlobalClose, true);
      window.removeEventListener("keydown", handleEscapeClose);
    };
  }, [miniMonthContextMenu]);


  const loadInvoiceTable = useCallback(async () => {
    setInvoiceLoading(true);
    setInvoiceError("");

    try {
      const payload = await fetchOutstandingInvoices();
      setInvoiceItems(Array.isArray(payload?.invoices) ? payload.invoices : []);
      setInvoiceGeneratedAt(payload?.generated_at || "");
    } catch (err) {
      // If auth token was cleared by global 401 handling, avoid surfacing raw API errors.
      if (!getAuthToken()) return;
      setInvoiceError(err.message || "Could not load invoices");
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoryError("");
    try {
      const payload = await fetchCategories();
      setCategories(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setCategoryError(err.message || "Could not load categories");
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!showInvoiceTable) return;
    loadInvoiceTable();
  }, [showInvoiceTable, loadInvoiceTable]);


  function matchesSearch(event, term) {
    if (!term) return true;
    const text = term.toLowerCase();

    return (
      (event.title && event.title.toLowerCase().includes(text)) ||
      (event.project_name && event.project_name.toLowerCase().includes(text)) ||
      (event.location && event.location.toLowerCase().includes(text)) ||
      (event.invoice_notes && event.invoice_notes.toLowerCase().includes(text)) ||
      (event.internal_notes && event.internal_notes.toLowerCase().includes(text))
    );
  }

  const validateTimes = () => {
    if (modalData.all_day) return !!modalData.start;
    if (!modalData.start || !modalData.end) return false;
    return new Date(modalData.start) < new Date(modalData.end);
  };

  const handleCreateCategory = async (payload) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await createCategory(payload);
      await loadCategories();
    } catch (err) {
      setCategoryError(err.message || "Could not create category");
    } finally {
      setCategorySaving(false);
    }
  };

  const handleUpdateCategory = async (categoryId, payload) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await updateCategory(categoryId, payload);

      const normalizedCategoryId = String(categoryId || "");
      const normalizedName = String(payload?.name || "").trim();
      const normalizedColor = String(payload?.color || "").trim();

      const targets = events.filter(
        (ev) => String(ev.extendedProps?.category_id || "") === normalizedCategoryId
      );

      if (targets.length > 0) {
        await Promise.all(
          targets.map((ev) =>
            updateSilently(ev.id, {
              extendedProps: {
                category_id: normalizedCategoryId,
                category_name: normalizedName,
                category_color: normalizedColor,
              },
            })
          )
        );

        await reload();
      }

      await loadCategories();

      setModalData((prev) => {
        if (!prev?.category_id || prev.category_id !== categoryId) return prev;
        return {
          ...prev,
          category_name: payload?.name || prev.category_name || "",
          category_color: payload?.color || prev.category_color || "",
        };
      });
    } catch (err) {
      setCategoryError(err.message || "Could not update category");
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await deleteCategory(categoryId);
      await loadCategories();

      setModalData((prev) => {
        if (!prev?.category_id || prev.category_id !== categoryId) return prev;
        return {
          ...prev,
          category_id: "",
          category_name: "",
          category_color: "",
        };
      });
    } catch (err) {
      setCategoryError(err.message || "Could not delete category");
    } finally {
      setCategorySaving(false);
    }
  };

  const roundTo30 = (date) => {
    const roundedDate = new Date(date);
    const minutes = roundedDate.getMinutes();
    const roundedMinutes = Math.round(minutes / 30) * 30;
    roundedDate.setMinutes(roundedMinutes);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);
    return roundedDate;
  };

  const getRecurrenceMetadata = (currentProps = {}, fallbackProps = {}) => {
    const series_id = currentProps.series_id ?? fallbackProps.series_id ?? "";
    const repeat_rule = currentProps.repeat_rule ?? fallbackProps.repeat_rule ?? "";
    const repeat_until = currentProps.repeat_until ?? fallbackProps.repeat_until ?? "";
    const repeat_interval = Number(
      currentProps.repeat_interval ?? fallbackProps.repeat_interval ?? 1
    );
    const repeat_end_mode =
      currentProps.repeat_end_mode ||
      fallbackProps.repeat_end_mode ||
      "frequency";
    const repeat_periods = Number(
      currentProps.repeat_periods ?? fallbackProps.repeat_periods ?? 3
    );

    return {
      series_id,
      repeat_rule,
      repeat_until,
      repeat_interval: Number.isFinite(repeat_interval) && repeat_interval > 0 ? repeat_interval : 1,
      repeat_end_mode,
      repeat_periods: Number.isFinite(repeat_periods) && repeat_periods > 0 ? repeat_periods : 3,
    };
  };

  const buildRecurringPayloadsFromModal = useCallback((modalInput, basePayload) => {
    const repeatRule = modalInput.repeat_rule || "none";
    const repeatInterval = Math.max(1, Number(modalInput.repeat_interval || 1));
    const repeatEndMode =
      modalInput.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatPeriods = Math.max(1, Number(modalInput.repeat_periods || 3));

    if (repeatRule === "none") return [basePayload];

    const addMonthsClamped = (date, months, anchorDay) => {
      const shifted = new Date(date);
      shifted.setDate(1);
      shifted.setMonth(shifted.getMonth() + months);
      const monthLastDay = new Date(
        shifted.getFullYear(),
        shifted.getMonth() + 1,
        0
      ).getDate();
      shifted.setDate(Math.min(anchorDay, monthLastDay));
      return shifted;
    };

    const pad2 = (n) => String(n).padStart(2, "0");
    const toDateOnly = (date) =>
      `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    const toDateTimeSeconds = (date) =>
      `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
        date.getHours()
      )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;

    const normalizeRecurringReminderConfig = (rawReminderConfig, occurrenceDateOnly) => {
      if (!rawReminderConfig || typeof rawReminderConfig !== "string") {
        return rawReminderConfig || "";
      }

      try {
        const parsed = JSON.parse(rawReminderConfig);
        if (!parsed || typeof parsed !== "object") return rawReminderConfig;

        const pattern = String(parsed.pattern || "one_time_date").trim();
        if (pattern !== "one_time_date") return rawReminderConfig;

        return JSON.stringify({
          ...parsed,
          pattern: "one_time_date",
          reminder_date: occurrenceDateOnly,
          one_time_date: occurrenceDateOnly,
        });
      } catch {
        return rawReminderConfig;
      }
    };

    const untilDate =
      repeatEndMode === "until" && String(modalInput.repeat_until || "").trim()
        ? new Date(`${modalInput.repeat_until}T23:59:59`)
        : null;
    const startBase = modalInput.all_day
      ? new Date(`${basePayload.start}T00:00:00`)
      : new Date(basePayload.start);
    const endBase = modalInput.all_day
      ? new Date(`${basePayload.end}T00:00:00`)
      : new Date(basePayload.end);

    const anchorStartDay = startBase.getDate();
    const anchorEndDay = endBase.getDate();
    const anchorWeekday = startBase.getDay();
    const anchorNthWeekday = Math.floor((startBase.getDate() - 1) / 7) + 1;

    const monthlyNthWeekday = (date, nth, weekday) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const dayCount = new Date(year, month + 1, 0).getDate();
      const matches = [];
      for (let day = 1; day <= dayCount; day += 1) {
        if (new Date(year, month, day).getDay() === weekday) {
          matches.push(day);
        }
      }
      const idx = Math.max(0, Math.min(matches.length - 1, nth - 1));
      const chosenDay = matches[idx] || 1;
      return new Date(year, month, chosenDay);
    };

    const advanceCursor = (startDate, endDate) => {
      if (repeatRule === "weekly" || repeatRule === "weekly_specific_day" || repeatRule === "weekly_edge_first" || repeatRule === "weekly_edge_last") {
        startDate.setDate(startDate.getDate() + 7 * repeatInterval);
        endDate.setDate(endDate.getDate() + 7 * repeatInterval);
        return;
      }

      if (repeatRule === "monthly" || repeatRule === "monthly_specific_day") {
        const nextStart = addMonthsClamped(startDate, repeatInterval, anchorStartDay);
        const nextEnd = addMonthsClamped(endDate, repeatInterval, anchorEndDay);
        startDate.setTime(nextStart.getTime());
        endDate.setTime(nextEnd.getTime());
        return;
      }

      if (repeatRule === "monthly_edge_first") {
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() + repeatInterval);
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }

      if (repeatRule === "monthly_edge_last") {
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() + repeatInterval + 1);
        startDate.setDate(0);
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }

      if (repeatRule === "monthly_nth_weekday") {
        const monthCursor = new Date(startDate);
        monthCursor.setDate(1);
        monthCursor.setMonth(monthCursor.getMonth() + repeatInterval);
        const nextStart = monthlyNthWeekday(monthCursor, anchorNthWeekday, anchorWeekday);
        startDate.setTime(nextStart.getTime());
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }

      if (repeatRule === "yearly" || repeatRule === "yearly_date") {
        startDate.setFullYear(startDate.getFullYear() + repeatInterval);
        endDate.setFullYear(endDate.getFullYear() + repeatInterval);
        return;
      }

      startDate.setDate(startDate.getDate() + repeatInterval);
      endDate.setDate(endDate.getDate() + repeatInterval);
    };

    const getForeverWindowCount = () => {
      if (repeatRule === "yearly" || repeatRule === "yearly_date") return 25;
      if (String(repeatRule).startsWith("monthly_")) return 36;
      if (String(repeatRule).startsWith("weekly_")) return 78;
      return 120;
    };

    if (
      Number.isNaN(startBase.getTime()) ||
      Number.isNaN(endBase.getTime()) ||
      (untilDate && Number.isNaN(untilDate.getTime()))
    ) {
      return [basePayload];
    }

    const recurringPayloads = [];
    const cursorStart = new Date(startBase);
    const cursorEnd = new Date(endBase);
    const maxOccurrences =
      repeatEndMode === "until"
        ? 260
        : repeatEndMode === "forever"
        ? getForeverWindowCount()
        : repeatPeriods;

    for (let i = 0; i < maxOccurrences; i += 1) {
      if (untilDate && cursorStart > untilDate) break;

      const occurrenceStartValue =
        modalInput.all_day ? toDateOnly(cursorStart) : toDateTimeSeconds(cursorStart);
      const occurrenceEndValue =
        modalInput.all_day ? toDateOnly(cursorEnd) : toDateTimeSeconds(cursorEnd);
      const occurrenceDateOnly = toDateOnly(cursorStart);

      const reminderEnabled = basePayload.extendedProps?.reminder_enabled === true;
      const nextReminderConfig = reminderEnabled
        ? normalizeRecurringReminderConfig(
            basePayload.extendedProps?.reminder_config || "",
            occurrenceDateOnly
          )
        : basePayload.extendedProps?.reminder_config || "";

      recurringPayloads.push({
        ...basePayload,
        start: occurrenceStartValue,
        end: occurrenceEndValue,
        extendedProps: {
          ...(basePayload.extendedProps || {}),
          reminder_config: nextReminderConfig,
        },
      });

      advanceCursor(cursorStart, cursorEnd);
    }

    return recurringPayloads.length > 0 ? recurringPayloads : [basePayload];
  }, []);

  const parseDateOnly = (value) => {
    const [y, m, d] = String(value || "").slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const addDays = (date, days) => {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() + days);
    return shifted;
  };

  const formatDateTime12 = (dateTime) => dateTime.toFormat("hh.mma").toLowerCase();

  const parse24HourLabel = (label) => {
    const match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(label || "");
    if (!match) return { hour24: 0, minute: 0 };

    return {
      hour24: Number(match[1]),
      minute: Number(match[2]),
    };
  };

  const getLondonRowDateTime = (calendarApi, labelText) => {
    const row = parse24HourLabel(labelText);
    const anchorDate =
      calendarApi?.view?.currentStart || calendarApi?.getDate?.() || new Date();
    const londonAnchor = DateTime.fromJSDate(anchorDate, { zone: "Europe/London" });

    return londonAnchor.set({
      hour: row.hour24,
      minute: row.minute,
      second: 0,
      millisecond: 0,
    });
  };

  const renderSidebarHeader = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const viewType = calendarApi.view?.type || "";
    if (!viewType.startsWith("timeGrid")) return;

    const calendarEl =
      calendarApi.el ||
      document.querySelector(".calendar-card .fc") ||
      document.querySelector(".fc");
    if (!calendarEl) return;

    const axisFrame =
      calendarEl.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis .fc-timegrid-axis-frame"
      ) ||
      calendarEl.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis .fc-scrollgrid-shrink-frame"
      ) ||
      calendarEl.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis .fc-timegrid-axis-cushion"
      ) ||
      calendarEl.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis .fc-scrollgrid-shrink-cushion"
      ) ||
      calendarEl.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis"
      );

    if (!axisFrame) return;

    let header = axisFrame.querySelector(".tz-sidebar-header");
    if (!header) {
      header = document.createElement("div");
      header.className = "tz-sidebar-header";

      const left = document.createElement("span");
      left.className = "tz-sidebar-header-left";
      const right = document.createElement("span");
      right.className = "tz-sidebar-header-right";

      header.appendChild(left);
      header.appendChild(right);

      const existingText = axisFrame.querySelector(
        ".fc-scrollgrid-shrink-cushion, .fc-timegrid-axis-cushion"
      );

      if (existingText) {
        existingText.replaceChildren(header);
      } else {
        axisFrame.replaceChildren(header);
      }
    }

    const left = header.querySelector(".tz-sidebar-header-left");
    const right = header.querySelector(".tz-sidebar-header-right");

    if (left) left.textContent = secondaryTimezoneOption.label;
    if (right) right.textContent = "UK";
  }, [secondaryTimezoneOption.label]);

  const renderSidebarAllDayRow = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const viewType = calendarApi.view?.type || "";
    if (!viewType.startsWith("timeGrid")) return;

    const calendarEl =
      calendarApi.el ||
      document.querySelector(".calendar-card .fc") ||
      document.querySelector(".fc");
    if (!calendarEl) return;

    const normalizeText = (value) =>
      (value || "").toLowerCase().replace(/[^a-z]/g, "");

    // Match all common spellings: all-day, all day, allday.
    const allDayLabel = Array.from(calendarEl.querySelectorAll("*"))
      .find((el) => normalizeText(el.textContent) === "allday");

    const axisCell =
      allDayLabel?.closest(".fc-timegrid-axis") ||
      allDayLabel?.closest("td") ||
      allDayLabel?.closest("th") ||
      calendarEl.querySelector("td.fc-timegrid-axis") ||
      calendarEl.querySelector("th.fc-timegrid-axis") ||
      calendarEl.querySelector("[class*='timegrid-axis']") ||
      null;
    if (!axisCell) return;

    axisCell.style.position = axisCell.style.position || "relative";
    axisCell.style.overflow = "visible";

    let divider = axisCell.querySelector(".tz-sidebar-allday-divider");
    if (!divider) {
      divider = document.createElement("span");
      divider.className = "tz-sidebar-allday-divider";
      axisCell.appendChild(divider);
    }

    divider.style.position = "absolute";
    divider.style.top = "0";
    divider.style.bottom = "0";
    divider.style.left = "50%";
    divider.style.width = "2px";
    divider.style.transform = "translateX(-1px)";
    divider.style.background = "var(--tz-divider-color)";
    divider.style.display = "block";
    divider.style.zIndex = "9999";
    divider.style.pointerEvents = "none";
  }, []);

  const scheduleSidebarDecorators = useCallback(() => {
    [0, 40, 120, 280, 650].forEach((delay) => {
      setTimeout(() => {
        renderSidebarHeader();
        renderSidebarAllDayRow();
      }, delay);
    });
  }, [renderSidebarHeader, renderSidebarAllDayRow]);

  useEffect(() => {
    scheduleSidebarDecorators();
  }, [scheduleSidebarDecorators]);

  const openNewEventModal = (info) => {
    const pad = (n) => String(n).padStart(2, "0");
    const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (info.allDay) {
      const startStr = toDateStr(info.start);
      // FullCalendar all-day selections are end-exclusive; store inclusive end in modal state.
      const inclusiveEnd = info.end
        ? addDays(parseDateOnly(toDateStr(info.end)), -1)
        : info.start;
      const endStr = toDateStr(inclusiveEnd || info.start);

      setModalData({
        ...emptyEvent,
        create_request_id: newCreateRequestId(),
        all_day: true,
        start: startStr,
        end: endStr,
      });
    } else {
      const format = (d) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
          d.getHours()
        )}:${pad(d.getMinutes())}`;

      setModalData({
        ...emptyEvent,
        create_request_id: newCreateRequestId(),
        start: format(info.start),
        end: format(info.end),
      });
    }

    setIsEdit(false);
    setModalOpen(true);
  };

  const openEditForEvent = (event) => {
    const calendarApi = calendarRef.current?.getApi();
    const activeViewType = calendarApi?.view?.type || "";
    const fallbackDate = calendarApi?.getDate?.() || event.start || new Date();
    const anchorDate = String(event.startStr || fallbackDate.toISOString?.() || "").slice(0, 10);
    editReturnContextRef.current = {
      viewType: activeViewType,
      anchorDate,
    };

    const allDayInclusiveEnd = () => {
      if (!event.end) return event.startStr;
      const excl = new Date(event.end);
      excl.setDate(excl.getDate() - 1);
      const pad = (n) => String(n).padStart(2, "0");
      return `${excl.getFullYear()}-${pad(excl.getMonth() + 1)}-${pad(excl.getDate())}`;
    };
    setModalData({
      id: event.id,
      title: event.title || "",
      create_request_id: event.extendedProps?.create_request_id || "",
      all_day: event.allDay,
      start: event.startStr,
      end: event.allDay ? allDayInclusiveEnd() : event.endStr,
      repeat_rule: event.extendedProps?.repeat_rule || (event.extendedProps?.series_id ? "daily" : "none"),
      repeat_until: event.extendedProps?.repeat_until || "",
      repeat_interval: Number(event.extendedProps?.repeat_interval || 1),
      repeat_end_mode: event.extendedProps?.repeat_end_mode || "",
      repeat_periods: Number(event.extendedProps?.repeat_periods || 3),
      is_lesson: event.extendedProps?.is_lesson ?? false,
      no_show: event.extendedProps?.no_show ?? false,
      fb_time_entry_id: event.extendedProps?.fb_time_entry_id ?? "",
      project_id: event.extendedProps?.project_id ?? null,
      project_name: event.extendedProps?.project_name ?? "",
      invoice_notes: event.extendedProps?.invoice_notes ?? "",
      internal_notes: event.extendedProps?.internal_notes ?? "",
      location: event.extendedProps?.location ?? "",
      category_id: event.extendedProps?.category_id ?? "",
      category_name: event.extendedProps?.category_name ?? "",
      category_color: event.extendedProps?.category_color ?? "",
      reminder_enabled: event.extendedProps?.reminder_enabled ?? false,
      reminder_config: event.extendedProps?.reminder_config ?? "",
      attachments: Array.isArray(event.extendedProps?.attachments)
        ? event.extendedProps.attachments
            .map((item) => ({
              label: String(item?.label || "").trim(),
              url: String(item?.url || "").trim(),
            }))
            .filter((item) => item.url)
        : [],
      series_id: event.extendedProps?.series_id ?? "",
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleEventClick = (info) => {
    const event = info.event;
    if (event.extendedProps?.isHoliday === true) return;
    const seriesId = event.extendedProps?.series_id;
    console.log("🔍 Event clicked:", { title: event.title, series_id: seriesId, extendedProps: event.extendedProps });
    if (seriesId) {
      setSeriesModalInfo({ event, seriesId });
    } else {
      openEditForEvent(event);
    }
  };

  const handleSeriesEditThis = () => {
    const { event } = seriesModalInfo;
    setSeriesModalInfo(null);
    openEditForEvent(event);
  };

  const handleSeriesDeleteThis = async () => {
    const { event } = seriesModalInfo;
    const perf = startPerfSpan("event.delete.series.this", {
      eventId: String(event?.id || ""),
      seriesId: String(event?.extendedProps?.series_id || ""),
    });
    setSeriesModalInfo(null);
    try {
      await remove(event.id);
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      throw err;
    }
  };

  const handleSeriesDeleteFollowing = async () => {
    const { event, seriesId } = seriesModalInfo;
    const fromDate = event.startStr.slice(0, 10);
    const perf = startPerfSpan("event.delete.series.following", {
      eventId: String(event?.id || ""),
      seriesId: String(seriesId || ""),
      fromDate,
    });

    setSeriesModalInfo(null);
    try {
      await removeSeriesFrom(seriesId, fromDate);
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      throw err;
    }
  };

  const handleSeriesDeleteAll = async () => {
    const { seriesId } = seriesModalInfo;
    const perf = startPerfSpan("event.delete.series.all", {
      seriesId: String(seriesId || ""),
    });

    setSeriesModalInfo(null);
    try {
      await removeSeries(seriesId);
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      throw err;
    }
  };

  const handleSave = async (locationOverride = null) => {
    if (isSaving) return;
    if (!validateTimes()) return;
    setIsSaving(true);
    const perf = startPerfSpan("event.save.create", {
      allDay: modalData.all_day === true,
      isLesson: modalData.is_lesson === true,
      repeatRule: String(modalData.repeat_rule || "none"),
    });

    try {

    const repeatRule = modalData.repeat_rule || "none";
    const repeatEndMode =
      modalData.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatInterval = Math.max(1, Number(modalData.repeat_interval || 1));
    const repeatValidationError = getRepeatValidationError({
      repeatRule,
      repeatEndMode,
      repeatUntil: modalData.repeat_until,
      start: modalData.start,
    });
    if (repeatValidationError) {
      setIsSaving(false);
      window.alert(repeatValidationError);
      return;
    }

    const isLesson = modalData.is_lesson === true;
    const notes = modalData.no_show
      ? `[No Show] ${modalData.invoice_notes || ""}`.trim()
      : modalData.invoice_notes || "";
    const selectedCategory = categories.find((item) => item.id === (modalData.category_id || ""));
    const resolvedCategoryName = modalData.category_id
      ? selectedCategory?.name || modalData.category_name || ""
      : "";
    const resolvedCategoryColor = modalData.category_id
      ? selectedCategory?.color || modalData.category_color || ""
      : "";

    let payloadStart, payloadEnd;
    if (modalData.all_day) {
      payloadStart = modalData.start;
      const d = new Date(modalData.end + "T00:00:00");
      d.setDate(d.getDate() + 1);
      const pad = (n) => String(n).padStart(2, "0");
      payloadEnd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } else {
      payloadStart = modalData.start.length === 16 ? `${modalData.start}:00` : modalData.start;
      payloadEnd = modalData.end.length === 16 ? `${modalData.end}:00` : modalData.end;
    }

    const seriesId = repeatRule !== "none" ? crypto.randomUUID() : "";

    const resolvedLocation =
      typeof locationOverride === "string"
        ? locationOverride
        : modalData.location || "";

    const resolvedAttachments = Array.isArray(modalData.attachments)
      ? modalData.attachments
          .map((item) => ({
            label: String(item?.label || "").trim(),
            url: String(item?.url || "").trim(),
          }))
          .filter((item) => item.url)
      : [];

    const payload = {
      title: modalData.title,
      start: payloadStart,
      end: payloadEnd,

      extendedProps: {
        project_id: modalData.project_id
          ? String(modalData.project_id)
          : "",
        project_name: modalData.project_name || "",
        is_lesson: isLesson,
        no_show: modalData.no_show === true,
        fb_time_entry_id: "",
        invoice_notes: modalData.invoice_notes || "",
        internal_notes: modalData.internal_notes || "",
        location: resolvedLocation,
        category_id: modalData.category_id || "",
        category_name: resolvedCategoryName,
        category_color: resolvedCategoryColor,
        reminder_enabled: modalData.reminder_enabled === true,
        reminder_config: modalData.reminder_config || "",
        attachments: resolvedAttachments,
        series_id: seriesId,
        repeat_rule: repeatRule,
        repeat_until: modalData.repeat_until || "",
        repeat_interval: repeatInterval,
        repeat_end_mode: modalData.repeat_end_mode || (modalData.repeat_until ? "until" : "frequency"),
        repeat_periods: Math.max(1, Number(modalData.repeat_periods || 3)),
      },
    };

    const payloadsToCreate = buildRecurringPayloadsFromModal(modalData, payload);
    perf.step("payloads.built", { occurrences: payloadsToCreate.length });

    console.log("📤 Creating recurring events with payload template:", { 
      series_id: payload.extendedProps.series_id,
      repeat_rule: payload.extendedProps.repeat_rule,
      repeat_until: payload.extendedProps.repeat_until,
      count: payloadsToCreate.length 
    });

    const baseCreateRequestId = String(modalData.create_request_id || newCreateRequestId());
    const createStartedAt = getPerfNowMs();
    let fbSyncCount = 0;
    let fbSyncMsTotal = 0;
    for (let index = 0; index < payloadsToCreate.length; index += 1) {
      const occurrencePayload = payloadsToCreate[index];
      const occurrenceCreateRequestId = `${baseCreateRequestId}:${index}`;
      occurrencePayload.extendedProps = {
        ...(occurrencePayload.extendedProps || {}),
        create_request_id: occurrenceCreateRequestId,
      };
      const newEvent = await createSilently(occurrencePayload);

      if (!modalData.all_day && isLesson && newEvent?.id) {
        try {
          const fbSyncStartedAt = getPerfNowMs();
          await submitTimeEntry({
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: occurrencePayload.start,
            eventEnd: occurrencePayload.end,
            notes,
            eventId: newEvent.id,
          });
          fbSyncCount += 1;
          fbSyncMsTotal += getPerfNowMs() - fbSyncStartedAt;
        } catch (err) {
          window.alert(`Lesson saved but Freshbooks sync failed:\n${err.message}`);
        }
      }
    }
    perf.step("events.created", {
      occurrences: payloadsToCreate.length,
      elapsedMs: roundPerfMs(getPerfNowMs() - createStartedAt),
    });
    if (fbSyncCount > 0) {
      perf.step("freshbooks.synced", {
        count: fbSyncCount,
        elapsedMs: roundPerfMs(fbSyncMsTotal),
      });
    }
    setModalOpen(false);
    setModalData({});
    const reloadStartedAt = getPerfNowMs();
    void reload()
      .then(() => {
        perf.step("reload.completed", { elapsedMs: roundPerfMs(getPerfNowMs() - reloadStartedAt) });
      })
      .catch(() => {
        perf.step("reload.failed", { elapsedMs: roundPerfMs(getPerfNowMs() - reloadStartedAt) });
      });
    perf.step("reload.deferred");
    perf.end("ok", { occurrences: payloadsToCreate.length });
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      window.alert(`Could not save event:\n${err?.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (locationOverride = null) => {
    if (isSaving) return;
    if (!validateTimes()) return;

    const repeatRule = modalData.repeat_rule || "none";
    const repeatEndMode =
      modalData.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatValidationError = getRepeatValidationError({
      repeatRule,
      repeatEndMode,
      repeatUntil: modalData.repeat_until,
      start: modalData.start,
    });
    if (repeatValidationError) {
      window.alert(repeatValidationError);
      return;
    }

    setIsSaving(true);
    const perf = startPerfSpan("event.save.update", {
      eventId: String(modalData.id || ""),
      allDay: modalData.all_day === true,
      isLesson: modalData.is_lesson === true,
      repeatRule: String(modalData.repeat_rule || "none"),
    });

    try {

    const isLesson = modalData.is_lesson === true;
    const fbId = modalData.fb_time_entry_id || "";
    const notes = modalData.no_show
      ? `[No Show] ${modalData.invoice_notes || ""}`.trim()
      : modalData.invoice_notes || "";
    const selectedCategory = categories.find((item) => item.id === (modalData.category_id || ""));
    const resolvedCategoryName = modalData.category_id
      ? selectedCategory?.name || modalData.category_name || ""
      : "";
    const resolvedCategoryColor = modalData.category_id
      ? selectedCategory?.color || modalData.category_color || ""
      : "";

    // If lesson was turned off but had a Freshbooks entry, delete it
    let fbTimeEntryId = fbId;
    if (!isLesson && fbId) {
      try {
        await deleteTimeEntry(fbId);
        fbTimeEntryId = "";
      } catch (err) {
        window.alert(`Could not delete Freshbooks entry:\n${err.message}`);
      }
    }

    let payloadStart, payloadEnd;
    if (modalData.all_day) {
      payloadStart = modalData.start;
      const d = new Date(modalData.end + "T00:00:00");
      d.setDate(d.getDate() + 1);
      const pad = (n) => String(n).padStart(2, "0");
      payloadEnd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } else {
      payloadStart = modalData.start.length === 16 ? `${modalData.start}:00` : modalData.start;
      payloadEnd = modalData.end.length === 16 ? `${modalData.end}:00` : modalData.end;
    }

    const originalEvent = events.find((ev) => ev.id === modalData.id);
    const originalRecurring = getRecurrenceMetadata(originalEvent?.extendedProps);
    const resolvedRepeatRule =
      modalData.repeat_rule === undefined || modalData.repeat_rule === null || modalData.repeat_rule === ""
        ? originalRecurring.repeat_rule || "none"
        : modalData.repeat_rule;
    const resolvedRepeatEndMode =
      modalData.repeat_end_mode || (resolvedRepeatRule === "forever" ? "forever" : "frequency");
    const resolvedRepeatUntil =
      resolvedRepeatRule === "forever" || resolvedRepeatEndMode === "forever"
        ? ""
        : modalData.repeat_until === undefined || modalData.repeat_until === null
        ? originalRecurring.repeat_until
        : modalData.repeat_until;
    const resolvedRepeatInterval =
      resolvedRepeatRule === "none"
        ? 1
        : Math.max(
            1,
            Number(
              modalData.repeat_interval === undefined || modalData.repeat_interval === null
                ? originalRecurring.repeat_interval
                : modalData.repeat_interval
            ) || 1
          );
    const resolvedReminderConfig =
      modalData.reminder_enabled === true
        ? modalData.reminder_config || originalEvent?.extendedProps?.reminder_config || ""
        : "";
    const resolvedRepeatPeriods = Math.max(
      1,
      Number(modalData.repeat_periods ?? originalRecurring.repeat_periods ?? 3) || 3
    );
    const updatedSeriesId = resolvedRepeatRule === "none"
      ? ""
      : modalData.series_id || originalRecurring.series_id || crypto.randomUUID();

    const resolvedLocation =
      typeof locationOverride === "string"
        ? locationOverride
        : modalData.location || "";

    const resolvedAttachments = Array.isArray(modalData.attachments)
      ? modalData.attachments
          .map((item) => ({
            label: String(item?.label || "").trim(),
            url: String(item?.url || "").trim(),
          }))
          .filter((item) => item.url)
      : [];

    const payload = {
      title: modalData.title,
      start: payloadStart,
      end: payloadEnd,

      extendedProps: {
        project_id: modalData.project_id
          ? String(modalData.project_id)
          : "",
        project_name: modalData.project_name || "",
        is_lesson: isLesson,
        no_show: modalData.no_show === true,
        fb_time_entry_id: fbTimeEntryId,
        invoice_notes: modalData.invoice_notes || "",
        internal_notes: modalData.internal_notes || "",
        location: resolvedLocation,
        category_id: modalData.category_id || "",
        category_name: resolvedCategoryName,
        category_color: resolvedCategoryColor,
        reminder_enabled: modalData.reminder_enabled === true,
        reminder_config: resolvedReminderConfig,
        attachments: resolvedAttachments,
        series_id: updatedSeriesId,
        repeat_rule: resolvedRepeatRule || "none",
        repeat_until: resolvedRepeatUntil || "",
        repeat_interval: resolvedRepeatInterval,
        repeat_end_mode: resolvedRepeatEndMode,
        repeat_periods: resolvedRepeatPeriods,
      },
    };

    const askRecurringUpdateScope = async () => {
      if (!modalData.series_id) return "this";
      return requestRecurringScope("update");
    };

    const pad2 = (n) => String(n).padStart(2, "0");
    const toDateOnly = (value) => {
      const d = value instanceof Date ? value : new Date(value);
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    };

    const originalStart = originalEvent ? new Date(originalEvent.start) : new Date(payloadStart);
    const currentStart = new Date(payloadStart);
    const currentEnd = new Date(payloadEnd);
    const startShiftMs = currentStart.getTime() - originalStart.getTime();
    const updatedDurationMs = currentEnd.getTime() - currentStart.getTime();

    const applySharedFieldsToEvent = (eventItem) => {
      const eventStart = new Date(eventItem.start);
      const shiftedStart = new Date(eventStart.getTime() + startShiftMs);
      const shiftedEnd = new Date(shiftedStart.getTime() + updatedDurationMs);

      const targetStart = modalData.all_day ? toDateOnly(shiftedStart) : shiftedStart.toISOString();
      const targetEnd = modalData.all_day ? toDateOnly(shiftedEnd) : shiftedEnd.toISOString();

      return {
        title: modalData.title,
        start: targetStart,
        end: targetEnd,
        extendedProps: {
          project_id: modalData.project_id ? String(modalData.project_id) : "",
          project_name: modalData.project_name || "",
          is_lesson: isLesson,
          no_show: modalData.no_show === true,
          fb_time_entry_id: eventItem.extendedProps?.fb_time_entry_id || "",
          invoice_notes: modalData.invoice_notes || "",
          internal_notes: modalData.internal_notes || "",
          location: resolvedLocation,
          category_id: modalData.category_id || "",
          category_name: resolvedCategoryName,
          category_color: resolvedCategoryColor,
          reminder_enabled: modalData.reminder_enabled === true,
          reminder_config: resolvedReminderConfig,
          attachments: resolvedAttachments,
          series_id: updatedSeriesId,
          repeat_rule: resolvedRepeatRule || "none",
          repeat_until: resolvedRepeatUntil || "",
          repeat_interval: resolvedRepeatInterval,
          repeat_end_mode: resolvedRepeatEndMode,
          repeat_periods: resolvedRepeatPeriods,
        },
      };
    };

    const scope = await askRecurringUpdateScope();
    perf.step("scope.selected", { scope: String(scope || "") });
    if (scope === null) {
      setIsSaving(false);
      perf.end("cancelled");
      return;
    }

    const recurrenceChanged =
      resolvedRepeatRule !== (originalRecurring.repeat_rule || "none") ||
      resolvedRepeatUntil !== (originalRecurring.repeat_until || "") ||
      resolvedRepeatInterval !== (originalRecurring.repeat_interval || 1) ||
      resolvedRepeatEndMode !== (originalRecurring.repeat_end_mode || "frequency") ||
      resolvedRepeatPeriods !== (originalRecurring.repeat_periods || 3);

    // Turning on recurrence for a one-off event (no series_id yet) must also generate
    // the future occurrences, not just flag the existing single event as recurring.
    const isPromotingToSeries = !modalData.series_id && resolvedRepeatRule !== "none";

    if ((modalData.series_id && recurrenceChanged && scope !== "this") || isPromotingToSeries) {
      const anchorDate = String(payload.start || modalData.start || "").slice(0, 10);
      perf.step("series.regenerate.started", { scope, anchorDate, promoting: isPromotingToSeries });

      if (isPromotingToSeries) {
        // The original single event is superseded by the newly generated series occurrences.
        await remove(modalData.id);
      } else if (scope === "all") {
        await removeSeries(modalData.series_id);
      } else {
        await removeSeriesFrom(modalData.series_id, anchorDate);
      }

      const regeneratedPayloads = buildRecurringPayloadsFromModal(modalData, payload).filter((item) => {
        if (isPromotingToSeries || scope === "all") return true;
        return String(item.start || "").slice(0, 10) >= anchorDate;
      });

      let firstCreatedId = null;
      let firstCreatedStart = payload.start;
      const regeneratedStartedAt = getPerfNowMs();
      for (const occurrencePayload of regeneratedPayloads) {
        const newEvent = await createSilently(occurrencePayload);
        if (!firstCreatedId && newEvent?.id) {
          firstCreatedId = newEvent.id;
          firstCreatedStart = occurrencePayload.start;
        }

        if (!modalData.all_day && isLesson && newEvent?.id) {
          try {
            await submitTimeEntry({
              projectId: modalData.project_id,
              eventTitle: modalData.title,
              eventStart: occurrencePayload.start,
              eventEnd: occurrencePayload.end,
              notes,
              eventId: newEvent.id,
            });
          } catch (err) {
            window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
          }
        }
      }
      perf.step("series.regenerate.created", {
        occurrences: regeneratedPayloads.length,
        elapsedMs: roundPerfMs(getPerfNowMs() - regeneratedStartedAt),
      });

      const reloadStartedAt = getPerfNowMs();
      await reload();
      perf.step("reload.completed", { elapsedMs: roundPerfMs(getPerfNowMs() - reloadStartedAt) });
      setModalOpen(false);
      setModalData({});
      restoreViewAndFocusUpdatedEvent(firstCreatedId || modalData.id, firstCreatedStart);
      perf.end("ok", { regenerated: regeneratedPayloads.length, scope, promoting: isPromotingToSeries });
      return;
    }

    const updateStartedAt = getPerfNowMs();
    await update(modalData.id, payload);
    perf.step("event.updated", { elapsedMs: roundPerfMs(getPerfNowMs() - updateStartedAt) });

    if (scope === "following" && modalData.series_id) {
      const anchorDate = String(modalData.start || "").slice(0, 10);
      const following = events.filter((ev) => {
        if (ev.id === modalData.id) return false;
        if (ev.extendedProps?.series_id !== modalData.series_id) return false;
        return String(ev.start || "").slice(0, 10) >= anchorDate;
      });

      const followingStartedAt = getPerfNowMs();
      for (const ev of following) {
        await updateSilently(ev.id, applySharedFieldsToEvent(ev));

        const targetFbId = ev.extendedProps?.fb_time_entry_id || "";
        if (!modalData.all_day && isLesson && targetFbId) {
          try {
            const targetPayload = applySharedFieldsToEvent(ev);
            await updateTimeEntry(targetFbId, {
              projectId: modalData.project_id,
              eventTitle: modalData.title,
              eventStart: targetPayload.start,
              eventEnd: targetPayload.end,
              notes,
            });
          } catch (err) {
            window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
          }
        }
      }
      perf.step("following.updated", {
        count: following.length,
        elapsedMs: roundPerfMs(getPerfNowMs() - followingStartedAt),
      });
    }

    if (!modalData.all_day && isLesson) {
      if (fbTimeEntryId) {
        // Already in Freshbooks — update it
        try {
          await updateTimeEntry(fbTimeEntryId, {
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: payload.start,
            eventEnd: payload.end,
            notes,
          });

        } catch (err) {
          console.error("[updateTimeEntry] error:", err.message);
          window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
        }
      } else {
        // First time (legacy event or just became a lesson) — create it
        try {
          await submitTimeEntry({
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: payload.start,
            eventEnd: payload.end,
            notes,
            eventId: modalData.id,
          });
          reload();
        } catch (err) {
          console.error("[submitTimeEntry] error:", err.message);
          window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
        }
      }
    }

    const reloadStartedAt = getPerfNowMs();
    await reload();
    perf.step("reload.completed", { elapsedMs: roundPerfMs(getPerfNowMs() - reloadStartedAt) });

    const updatedEventId = modalData.id;
    const updatedEventStart = payload.start;

    setModalOpen(false);
    setModalData({});
    restoreViewAndFocusUpdatedEvent(updatedEventId, updatedEventStart);
    perf.end("ok", { scope: String(scope || "this") });
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      window.alert(`Could not update event:\n${err?.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    const perf = startPerfSpan("event.delete.single", {
      eventId: String(modalData.id || ""),
    });
    try {
      await remove(modalData.id);
      perf.step("event.removed");
      setModalOpen(false);
      setModalData({});
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      window.alert(`Could not delete event:\n${err?.message || err}`);
    }
  };

  const handleModalDeleteFollowing = async () => {
    if (!modalData.series_id) return;
    const fromDate = String(modalData.start || "").slice(0, 10);
    const perf = startPerfSpan("event.delete.modal.following", {
      eventId: String(modalData.id || ""),
      seriesId: String(modalData.series_id || ""),
      fromDate,
    });

    try {
      await removeSeriesFrom(modalData.series_id, fromDate);
      setModalOpen(false);
      setModalData({});
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      throw err;
    }
  };

  const handleModalDeleteAll = async () => {
    if (!modalData.series_id) return;
    const perf = startPerfSpan("event.delete.modal.all", {
      eventId: String(modalData.id || ""),
      seriesId: String(modalData.series_id || ""),
    });

    try {
      await removeSeries(modalData.series_id);
      setModalOpen(false);
      setModalData({});
      perf.end("ok");
    } catch (err) {
      perf.end("error", { message: String(err?.message || err) });
      throw err;
    }
  };

  const clearEventTooltips = () => {
    hideAll({ duration: 0 });
    document.querySelectorAll(".fc-event-mirror, .fc-event-dragging").forEach((el) => {
      if (el._tippy) {
        el._tippy.destroy();
      }
    });
  };

  function getElapsedBackgroundEvents() {
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0
    );

    return [
      {
        id: "elapsed-today",
        start: todayStart,
        end: now,
        display: "background",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
    ];
  }

  const filteredEvents = events.filter((ev) => matchesSearch(ev, searchTerm));
  const styledEvents = filteredEvents.map((ev) => {
    const categoryColor = String(ev.extendedProps?.category_color || "").trim();
    if (!categoryColorRegex.test(categoryColor)) {
      return ev;
    }

    return {
      ...ev,
      backgroundColor: hexToTint(categoryColor, 0.5),
      borderColor: categoryColor,
    };
  });
  const calendarEvents = [...styledEvents, ...holidayEvents, ...getElapsedBackgroundEvents()];
  const noVisibleEvents = filteredEvents.length === 0;
  const hasSearch = searchTerm.trim().length > 0;

  const lessonMinutesInView = filteredEvents.reduce((totalMinutes, ev) => {
    if (ev.extendedProps?.is_lesson !== true) return totalMinutes;

    const eventStart = ev.start ? new Date(ev.start) : null;
    const eventEnd = ev.end ? new Date(ev.end) : null;
    if (!eventStart || !eventEnd) return totalMinutes;

    const rangeStart = activeView.periodStart ? new Date(activeView.periodStart) : null;
    const rangeEnd = activeView.periodEnd ? new Date(activeView.periodEnd) : null;

    if (rangeStart && rangeEnd) {
      const overlapStart = eventStart > rangeStart ? eventStart : rangeStart;
      const overlapEnd = eventEnd < rangeEnd ? eventEnd : rangeEnd;

      if (overlapEnd <= overlapStart) return totalMinutes;

      const minutes = Math.max(0, Math.round((overlapEnd - overlapStart) / 60000));
      return totalMinutes + minutes;
    }

    const minutes = Math.max(0, Math.round((eventEnd - eventStart) / 60000));
    return totalMinutes + minutes;
  }, 0);

  const lessonHoursWhole = Math.floor(lessonMinutesInView / 60);
  const lessonMinutesRemainder = lessonMinutesInView % 60;
  const activeViewLabel =
    activeView.type === "timeGridWeek"
      ? "week"
      : activeView.type === "timeGridDay"
        ? "day"
        : "month";
  const lessonHoursText = `${lessonHoursWhole}h ${String(lessonMinutesRemainder).padStart(2, "0")}m`;
  const statsSummaryText = `Lesson hours (${activeViewLabel}): ${lessonHoursText}`;

  const formatInvoiceAmount = (amount, currencyCode) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return "-";

    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currencyCode || "GBP",
      }).format(numeric);
    } catch {
      return `${numeric.toFixed(2)} ${currencyCode || ""}`.trim();
    }
  };

  const formatInvoiceDueDate = (rawValue) => {
    const dt = DateTime.fromISO(String(rawValue || ""));
    if (!dt.isValid) return rawValue || "-";
    return dt.toFormat("dd LLL yyyy");
  };

  const formatInvoiceStatus = (item) => {
    const v3 = String(item?.v3_status || "").trim();
    return v3 || "-";
  };

  const generatedStamp = invoiceGeneratedAt
    ? DateTime.fromISO(invoiceGeneratedAt).toFormat("dd LLL yyyy, HH:mm")
    : "";

  const miniMonthEventSignals = useMemo(() => {
    const signalsByDate = new Map();
    const eventsForPreview = [...styledEvents, ...holidayEvents];

    const toDayDateTime = (value) => {
      if (!value) return null;
      if (typeof value === "string" && DATE_ONLY_VALUE_REGEX.test(value)) {
        return DateTime.fromISO(value, { zone: "Europe/London" }).startOf("day");
      }

      const asDate = value instanceof Date ? value : new Date(value);
      return DateTime.fromJSDate(asDate, { zone: "Europe/London" }).startOf("day");
    };

    for (const eventItem of eventsForPreview) {
      const startDay = toDayDateTime(eventItem.start);
      if (!startDay?.isValid) continue;

      const endDay = toDayDateTime(eventItem.end);
      const lastDay =
        endDay?.isValid && endDay > startDay
          ? endDay.minus({ days: 1 })
          : startDay;

      const isHoliday = eventItem.extendedProps?.isHoliday === true;
      const holidayCountry = String(eventItem.extendedProps?.holidayCountry || "").toUpperCase();
      const isLesson = eventItem.extendedProps?.is_lesson === true;
      const lessonBillingStatus = String(eventItem.extendedProps?.billing_status || "").trim().toLowerCase();
      const isLessonBilled = isLesson && lessonBillingStatus === "billed";
      const isLessonPaid = isLesson && lessonBillingStatus === "paid";
      const isAllDayEvent =
        eventItem.allDay === true ||
        eventItem.all_day === true ||
        eventItem.extendedProps?.allDay === true ||
        eventItem.extendedProps?.all_day === true ||
        (typeof eventItem.start === "string" && DATE_ONLY_VALUE_REGEX.test(eventItem.start));
      const eventTitle = String(eventItem.title || "").trim();

      // Compute lesson duration from start/end (timed events only)
      let lessonDurationMins = 0;
      if (isLesson && !isHoliday && !isAllDayEvent && eventItem.end) {
        const startDt =
          eventItem.start instanceof Date
            ? DateTime.fromJSDate(eventItem.start)
            : DateTime.fromISO(eventItem.start);
        const endDt =
          eventItem.end instanceof Date
            ? DateTime.fromJSDate(eventItem.end)
            : DateTime.fromISO(eventItem.end);
        if (startDt?.isValid && endDt?.isValid) {
          lessonDurationMins = Math.max(0, endDt.diff(startDt, "minutes").minutes);
        }
      }

      for (let cursor = startDay; cursor <= lastDay; cursor = cursor.plus({ days: 1 })) {
        const key = cursor.toISODate();
        const existing =
          signalsByDate.get(key) || {
            count: 0,
            hasLesson: false,
            hasBilledLesson: false,
            hasPaidLesson: false,
            hasUkHoliday: false,
            hasSgHoliday: false,
            eventTitles: [],
            ukHolidayTitles: [],
            sgHolidayTitles: [],
            lessonMinutes: 0,
          };

        existing.count += 1;
        existing.hasLesson = existing.hasLesson || isLesson;
  existing.hasBilledLesson = existing.hasBilledLesson || isLessonBilled;
  existing.hasPaidLesson = existing.hasPaidLesson || isLessonPaid;
        existing.hasUkHoliday = existing.hasUkHoliday || (isHoliday && holidayCountry === UK_HOLIDAY_COUNTRY_CODE);
        existing.hasSgHoliday = existing.hasSgHoliday || (isHoliday && holidayCountry === SG_HOLIDAY_COUNTRY_CODE);

        if (isHoliday) {
          if (holidayCountry === UK_HOLIDAY_COUNTRY_CODE && eventTitle && !existing.ukHolidayTitles.includes(eventTitle)) {
            existing.ukHolidayTitles.push(eventTitle);
          } else if (holidayCountry === SG_HOLIDAY_COUNTRY_CODE && eventTitle && !existing.sgHolidayTitles.includes(eventTitle)) {
            existing.sgHolidayTitles.push(eventTitle);
          }
        } else if (eventTitle && existing.eventTitles.length < 5) {
          existing.eventTitles.push(eventTitle);
        }

        if (lessonDurationMins > 0) {
          existing.lessonMinutes += lessonDurationMins;
        }

        signalsByDate.set(key, existing);
      }
    }

    return signalsByDate;
  }, [styledEvents, holidayEvents]);

  const miniMonthAnchor =
    activeView.periodStart && DateTime.fromJSDate(activeView.periodStart).isValid
      ? DateTime.fromJSDate(activeView.periodStart, { zone: "Europe/London" })
          .startOf("month")
          .plus({ months: miniMonthOffset })
      : DateTime.now().setZone("Europe/London").startOf("month").plus({ months: miniMonthOffset });

  const miniMonthBlocks = useMemo(() => {
    const todayKey = DateTime.now().setZone("Europe/London").toISODate();
    const activeMonthKey = miniMonthAnchor.toFormat("yyyy-MM");

    return Array.from({ length: 3 }, (_, index) => {
      const monthStart = miniMonthAnchor.plus({ months: index });
      const monthEnd = monthStart.endOf("month");
      const gridStart = monthStart.minus({ days: monthStart.weekday - 1 });
      const gridEnd = monthEnd.plus({ days: 7 - monthEnd.weekday });
      const days = [];

      for (let cursor = gridStart; cursor <= gridEnd; cursor = cursor.plus({ days: 1 })) {
        const isoDate = cursor.toISODate();
        const signal = miniMonthEventSignals.get(isoDate) || {
          count: 0,
          hasLesson: false,
          hasBilledLesson: false,
          hasPaidLesson: false,
          hasUkHoliday: false,
          hasSgHoliday: false,
        };
        const eventCount = signal.count;
        days.push({
          isoDate,
          dayNumber: cursor.day,
          inMonth: cursor.month === monthStart.month,
          isToday: isoDate === todayKey,
          hasEvents: eventCount > 0,
          eventCount,
          hasLesson: signal.hasLesson,
          hasBilledLesson: signal.hasBilledLesson,
          hasPaidLesson: signal.hasPaidLesson,
          hasUkHoliday: signal.hasUkHoliday,
          hasSgHoliday: signal.hasSgHoliday,
          isWeekend: cursor.weekday >= 6,
          isHoliday: signal.hasUkHoliday || signal.hasSgHoliday,
        });
      }

      return {
        monthKey: monthStart.toFormat("yyyy-MM"),
        monthLabel: monthStart.toFormat("LLL yyyy"),
        monthStartIso: monthStart.toISODate(),
        isActive: monthStart.toFormat("yyyy-MM") === activeMonthKey,
        days,
      };
    });
  }, [miniMonthAnchor, miniMonthEventSignals]);

  const handleMiniMonthOffset = useCallback((delta) => {
    setMiniMonthOffset((current) => current + delta);
  }, []);

  const handleMiniMonthsCollapsedToggle = useCallback(() => {
    setMiniMonthsCollapsed((current) => {
      const next = !current;
      localStorage.setItem("miniMonthsCollapsed", String(next));
      return next;
    });
  }, []);

  const handleMiniMonthDayClick = useCallback((isoDate) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi.changeView("timeGridDay", isoDate);
  }, []);

  const handleMiniMonthWeekClick = useCallback((isoDate) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const anchorMonth = miniMonthAnchor.startOf("month");
    const targetMonth = DateTime.fromISO(isoDate, { zone: "Europe/London" })
      .startOf("week")
      .startOf("month");

    if (anchorMonth.isValid && targetMonth.isValid) {
      const anchorMonthIndex = anchorMonth.year * 12 + (anchorMonth.month - 1);
      const targetMonthIndex = targetMonth.year * 12 + (targetMonth.month - 1);
      setMiniMonthOffset(anchorMonthIndex - targetMonthIndex);
    }

    calendarApi.changeView("timeGridWeek", isoDate);
  }, [miniMonthAnchor]);

  const handleMiniMonthDayPrimaryAction = useCallback((isoDate) => {
    setMiniMonthContextMenu(null);
    handleMiniMonthWeekClick(isoDate);
  }, [handleMiniMonthWeekClick]);

  const handleMiniMonthDayContextMenu = useCallback((isoDate, event) => {
    event.preventDefault();
    clearTimeout(miniMonthHoverTimerRef.current);
    setMiniMonthHoverDay(null);
    setMiniMonthContextMenu({
      isoDate,
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  const handleMiniMonthTitleClick = useCallback((monthStartIso) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi.changeView("dayGridMonth", monthStartIso);
  }, []);

  const handleMiniMonthDayPointerEnter = useCallback((isoDate, event) => {
    clearTimeout(miniMonthHoverTimerRef.current);
    const rect = event.currentTarget.getBoundingClientRect();
    setMiniMonthHoverDay({ isoDate, rect });
  }, []);

  const handleMiniMonthDayPointerLeave = useCallback(() => {
    miniMonthHoverTimerRef.current = setTimeout(() => {
      setMiniMonthHoverDay(null);
    }, 150);
  }, []);

  const handleMiniMonthPopoverEnter = useCallback(() => {
    clearTimeout(miniMonthHoverTimerRef.current);
  }, []);

  const handleMiniMonthPopoverLeave = useCallback(() => {
    miniMonthHoverTimerRef.current = setTimeout(() => {
      setMiniMonthHoverDay(null);
    }, 150);
  }, []);

  return (
    <div className="calendar-page">
      <header className="calendar-page-header">
        <div className="calendar-header-brand">
          <h1 className="calendar-title">Watershed Moment</h1>
          <div className="calendar-url-hint" aria-label="Current calendar URL">
            {calendarUrlHint}
          </div>
        </div>
        <div className="calendar-header-actions" ref={headerMenuRef}>
          <div className="calendar-weather-wrap" ref={weatherPopoverRef}>
            <button
              type="button"
              className={`calendar-weather-chip${
                weatherSummary.alertLevel !== "none" ? ` calendar-weather-chip-state-${weatherSummary.alertLevel}` : ""
              }`}
              title={weatherSummary.subtitle || weatherSummary.temperatureText}
              aria-label={weatherSummary.subtitle || weatherSummary.temperatureText}
              aria-haspopup="dialog"
              aria-expanded={weatherPopoverOpen}
              onClick={() => setWeatherPopoverOpen((open) => !open)}
            >
              <span className="calendar-weather-chip-text">
                <WeatherIcon iconKey={weatherSummary.currentIcon} className="calendar-weather-chip-icon" />
                {weatherSummary.temperatureText}
              </span>
              <span className="calendar-weather-chip-location">{selectedUkWeatherCity.shortLabel}</span>
              {weatherSummary.alertLevel !== "none" ? (
                <span className={`calendar-weather-chip-alert calendar-weather-chip-alert-${weatherSummary.alertLevel}`}>
                  {weatherSummary.alertLevel === "alert" ? "Alert" : "Watch"}
                </span>
              ) : null}
            </button>
            {weatherPopoverOpen ? (
              <div id="calendar-weather-popover" className="calendar-weather-popover" role="dialog" aria-label={`Weather in ${selectedUkWeatherCity.label}`}>
                <div className="calendar-weather-popover-heading">{selectedUkWeatherCity.label}</div>
                <div className="calendar-weather-popover-summary">{weatherSummary.subtitle}</div>
                <div className="calendar-weather-popover-current-row">
                  <WeatherIcon iconKey={weatherSummary.currentIcon} className="calendar-weather-popover-current-icon" />
                  <span className="calendar-weather-popover-current-temp">{weatherSummary.temperatureText}</span>
                  <span className="calendar-weather-popover-current-wind">
                    Wind {weatherSummary.windText}
                    <WindDirectionIndicator
                      degrees={weatherSummary.windFlowDirectionDegrees}
                      className="calendar-weather-popover-wind-indicator"
                      title={`Wind from ${weatherSummary.windFromText}`}
                    />
                  </span>
                </div>
                <div className="calendar-weather-popover-risk-row">
                  <WeatherIcon iconKey={weatherSummary.nextRiskIcon} className="calendar-weather-popover-risk-icon" />
                  <span>{weatherSummary.nextRiskText}</span>
                </div>
                {weatherSummary.highLow ? (
                  <div className="calendar-weather-popover-highlow">{weatherSummary.highLow}</div>
                ) : null}
                {weatherSummary.alertLevel !== "none" ? (
                  <div className={`calendar-weather-alert-strip calendar-weather-alert-strip-${weatherSummary.alertLevel}`}>
                    {weatherSummary.alertText}
                  </div>
                ) : null}
                {weatherSummary.status === "loading" ? (
                  <div className="calendar-weather-popover-empty">Loading forecast...</div>
                ) : null}
                {weatherSummary.status === "error" ? (
                  <div className="calendar-weather-popover-empty">Weather data unavailable right now.</div>
                ) : null}
                {weatherSummary.status === "ready" && weatherSummary.forecastRows.length === 0 ? (
                  <div className="calendar-weather-popover-empty">No forecast points available.</div>
                ) : null}
                {weatherSummary.status === "ready" && weatherSummary.forecastRows.length > 0 ? (
                  <div className="calendar-weather-forecast-list" role="list" aria-label="Upcoming weather forecast">
                    {weatherSummary.forecastRows.map((row) => (
                      <div key={row.timeLabel} className="calendar-weather-forecast-row" role="listitem">
                        <span className="calendar-weather-forecast-time">{row.timeLabel}</span>
                        <span className="calendar-weather-forecast-temp">
                          <WeatherIcon iconKey={row.icon} className="calendar-weather-forecast-icon" />
                          {row.tempLabel}
                        </span>
                        <span className="calendar-weather-forecast-rain">{row.rainLabel}</span>
                        <span className="calendar-weather-forecast-wind">
                          <WindDirectionIndicator
                            degrees={row.windFlowDirectionDegrees}
                            className="calendar-weather-forecast-wind-indicator"
                            title={`Wind from ${row.windFromText}`}
                          />
                          {row.windLabel}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="calendar-stats-btn"
            title={statsSummaryText}
            aria-label={statsSummaryText}
          >
            Stats
            <span className="calendar-stats-btn-value">{lessonHoursText}</span>
          </button>
          <button
            type="button"
            className="calendar-options-btn"
            onClick={() => setHeaderMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={headerMenuOpen}
          >
            Options
          </button>
          {headerMenuOpen ? (
            <div className="calendar-options-menu" role="menu" aria-label="Calendar options">
              <button
                type="button"
                className="calendar-options-item"
                role="menuitem"
                onClick={() => {
                  onToggleUkHolidays();
                  setHeaderMenuOpen(false);
                }}
              >
                {showUkHolidays ? "Hide UK holidays" : "Show UK holidays"}
              </button>
              <button
                type="button"
                className="calendar-options-item"
                role="menuitem"
                onClick={() => {
                  onToggleSingaporeHolidays();
                  setHeaderMenuOpen(false);
                }}
              >
                {showSingaporeHolidays ? "Hide SG holidays" : "Show SG holidays"}
              </button>
              <div className="calendar-options-field" role="none">
                <label htmlFor="calendar-timezone-select">Timezone</label>
                <select
                  id="calendar-timezone-select"
                  className="calendar-options-select"
                  value={secondaryTimezone}
                  onChange={(event) => setSecondaryTimezone(event.target.value)}
                >
                  {sortedTimezoneOptions.map((option) => (
                    <option key={option.zone} value={option.zone}>{option.name}</option>
                  ))}
                </select>
              </div>
              <div className="calendar-options-field" role="none">
                <label htmlFor="calendar-weather-city-select">Weather city (UK)</label>
                <select
                  id="calendar-weather-city-select"
                  className="calendar-options-select"
                  value={ukWeatherCityId}
                  onChange={(event) => setUkWeatherCityId(event.target.value)}
                >
                  {sortedUkWeatherCities.map((city) => (
                    <option key={city.id} value={city.id}>{city.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="calendar-options-item"
                role="menuitem"
                aria-controls="invoice-overview-panel"
                onClick={() => {
                  setShowInvoiceTable((current) => !current);
                  setHeaderMenuOpen(false);
                }}
              >
                {showInvoiceTable ? "Hide invoices" : "Show invoices"}
              </button>
              {showInvoiceTable ? (
                <button
                  type="button"
                  className="calendar-options-item"
                  role="menuitem"
                  onClick={() => {
                    loadInvoiceTable();
                    setHeaderMenuOpen(false);
                  }}
                  disabled={invoiceLoading}
                >
                  {invoiceLoading ? "Refreshing..." : "Refresh invoices"}
                </button>
              ) : null}
              <button
                type="button"
                className="calendar-options-item"
                role="menuitem"
                onClick={() => {
                  setNotificationModalOpen(true);
                  setHeaderMenuOpen(false);
                }}
              >
                Notifications
              </button>
              <button
                type="button"
                className="calendar-options-item"
                role="menuitem"
                onClick={() => {
                  onTimeFormatChange?.(timeFormat === "24h" ? "12h" : "24h");
                  setHeaderMenuOpen(false);
                }}
              >
                {timeFormat === "24h" ? "Use 12-hour time" : "Use 24-hour time"}
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <section className="mini-month-toolbar" aria-label="Upcoming months overview">
        <div className="mini-month-toolbar-header">
          {!miniMonthsCollapsed ? (
            <div className="mini-month-toolbar-nav" role="group" aria-label="Calendar navigation">
              <button
                type="button"
                className="mini-month-nav-btn"
                onClick={() => handleMiniMonthOffset(-1)}
                aria-label="Show previous preview months"
              >
                ◀ Preview
              </button>
              <button
                type="button"
                className="mini-month-nav-btn"
                onClick={() => handleMiniMonthOffset(1)}
                aria-label="Show next preview months"
              >
                Preview ▶
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className="mini-month-collapse-btn"
            onClick={handleMiniMonthsCollapsedToggle}
            aria-expanded={!miniMonthsCollapsed}
            aria-controls="mini-month-cards"
          >
            {miniMonthsCollapsed ? "Show mini calendars" : "Hide mini calendars"}
          </button>
        </div>

        {!miniMonthsCollapsed ? (
          <div id="mini-month-cards" className="mini-month-cards-wrap">
            {miniMonthBlocks.map((block) => (
          <article
            key={block.monthKey}
            className={`mini-month-card ${block.isActive ? "is-active" : ""}`.trim()}
          >
            <button
              type="button"
              className="mini-month-title"
              onClick={() => handleMiniMonthTitleClick(block.monthStartIso)}
            >
              {block.monthLabel}
            </button>

            <div className="mini-month-weekdays" aria-hidden="true">
              {MINI_MONTH_WEEKDAY_LABELS.map((label, index) => (
                <span key={`${block.monthKey}-weekday-${index}`}>{label}</span>
              ))}
            </div>

            <div className="mini-month-grid" role="list" aria-label={`${block.monthLabel} quick view`}>
              {block.days.map((day) => (
                <button
                  key={day.isoDate}
                  type="button"
                  role="listitem"
                  className={[
                    "mini-month-day",
                    day.inMonth ? "" : "is-outside-month",
                    day.isToday ? "is-today" : "",
                    day.hasEvents ? "has-events" : "",
                    day.isWeekend ? "is-weekend" : "",
                    day.isHoliday ? "is-holiday" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={(e) => handleMiniMonthDayPrimaryAction(day.isoDate, e)}
                  onContextMenu={(e) => handleMiniMonthDayContextMenu(day.isoDate, e)}
                  onMouseEnter={(e) => handleMiniMonthDayPointerEnter(day.isoDate, e)}
                  onMouseLeave={handleMiniMonthDayPointerLeave}

                  aria-label={`${day.isoDate}${day.hasEvents ? `, ${day.eventCount} event${day.eventCount > 1 ? "s" : ""}` : ", no events"}`}
                >
                  <span className="mini-month-day-number">{day.dayNumber}</span>
                  {day.hasEvents ? (
                    <span className="mini-month-day-dots" aria-hidden="true">
                      {day.hasLesson ? (
                        <span
                          className={[
                            "mini-month-day-dot",
                            "mini-month-day-dot-lesson",
                            day.hasBilledLesson ? "mini-month-day-dot-lesson-billed" : "",
                            !day.hasBilledLesson && day.hasPaidLesson ? "mini-month-day-dot-lesson-paid" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        />
                      ) : null}
                      {day.hasUkHoliday ? <span className="mini-month-day-dot mini-month-day-dot-uk" /> : null}
                      {day.hasSgHoliday ? <span className="mini-month-day-dot mini-month-day-dot-sg" /> : null}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </article>
            ))}
          </div>
        ) : null}

        {miniMonthHoverDay ? (() => {
          const { isoDate, rect } = miniMonthHoverDay;
          const signal = miniMonthEventSignals.get(isoDate);
          const dateLabel = DateTime.fromISO(isoDate, { zone: "Europe/London" }).toFormat("ccc d LLL");

          const eventTitles = signal?.eventTitles || [];
          const ukHolidayTitles = signal?.ukHolidayTitles || [];
          const sgHolidayTitles = signal?.sgHolidayTitles || [];
          const lessonMinutes = signal?.lessonMinutes || 0;

          const displayed = eventTitles.slice(0, 3);
          const remaining = eventTitles.length - displayed.length;
          const lessonHours = Math.floor(lessonMinutes / 60);
          const lessonMins = lessonMinutes % 60;
          const lessonText =
            lessonHours > 0
              ? lessonMins > 0 ? `${lessonHours}h ${lessonMins}m` : `${lessonHours}h`
              : lessonMins > 0 ? `${lessonMins}m` : null;

          const hasAny = displayed.length > 0 || ukHolidayTitles.length > 0 || sgHolidayTitles.length > 0;

          const popoverLeft = Math.min(rect.left + rect.width / 2, window.innerWidth - 10);
          const popoverTop = rect.bottom + 6;

          return (
            <div
              className="mini-month-hover-popover"
              style={{ left: popoverLeft, top: popoverTop }}
              onMouseEnter={handleMiniMonthPopoverEnter}
              onMouseLeave={handleMiniMonthPopoverLeave}
              role="tooltip"
            >
              <div className="mini-month-hover-date">{dateLabel}</div>
              {!hasAny ? (
                <div className="mini-month-hover-empty">No events</div>
              ) : (
                <>
                  {ukHolidayTitles.map((t) => (
                    <div key={`uk-${t}`} className="mini-month-hover-item">
                      <span className="mini-month-hover-dot mini-month-hover-dot-uk" />
                      {t}
                    </div>
                  ))}
                  {sgHolidayTitles.map((t) => (
                    <div key={`sg-${t}`} className="mini-month-hover-item">
                      <span className="mini-month-hover-dot mini-month-hover-dot-sg" />
                      {t}
                    </div>
                  ))}
                  {displayed.map((t, i) => (
                    <div key={`ev-${i}`} className="mini-month-hover-item">
                      {t}
                    </div>
                  ))}
                  {remaining > 0 ? (
                    <div className="mini-month-hover-more">…and {remaining} more</div>
                  ) : null}
                  {lessonText ? (
                    <div className="mini-month-hover-lessons">
                      <span
                        className={[
                          "mini-month-hover-dot",
                          "mini-month-hover-dot-lesson",
                          signal?.hasBilledLesson ? "mini-month-hover-dot-lesson-billed" : "",
                          !signal?.hasBilledLesson && signal?.hasPaidLesson ? "mini-month-hover-dot-lesson-paid" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      />
                      {lessonText} lesson time
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
        })() : null}

        {miniMonthContextMenu ? (
          <div
            className="mini-month-context-menu"
            style={{
              left: Math.min(miniMonthContextMenu.x, window.innerWidth - 170),
              top: Math.min(miniMonthContextMenu.y, window.innerHeight - 92),
            }}
            onClick={(event) => event.stopPropagation()}
            role="menu"
            aria-label="Mini calendar actions"
          >
            <button
              type="button"
              className="mini-month-context-menu-item"
              onClick={() => {
                handleMiniMonthDayClick(miniMonthContextMenu.isoDate);
                setMiniMonthContextMenu(null);
              }}
              role="menuitem"
            >
              Open day view
            </button>
            <button
              type="button"
              className="mini-month-context-menu-item"
              onClick={() => {
                handleMiniMonthWeekClick(miniMonthContextMenu.isoDate);
                setMiniMonthContextMenu(null);
              }}
              role="menuitem"
            >
              Open week view
            </button>
          </div>
        ) : null}
      </section>

      {showInvoiceTable ? (
        <section id="invoice-overview-panel" className="invoice-overview-panel" aria-live="polite">
          <div className="invoice-overview-header">
            <div className="invoice-overview-heading-row">
              <h2>Outstanding Invoices</h2>
              {generatedStamp ? <span>Updated: {generatedStamp}</span> : null}
            </div>
            <button
              type="button"
              className="invoice-overview-close-btn"
              onClick={() => setShowInvoiceTable(false)}
              aria-label="Close invoices"
            >
              Close
            </button>
          </div>

          {invoiceError ? <div className="invoice-overview-error">{invoiceError}</div> : null}

          {invoiceLoading && !invoiceItems.length ? (
            <div className="invoice-overview-loading">Loading invoices...</div>
          ) : null}

          {!invoiceLoading && !invoiceError && invoiceItems.length === 0 ? (
            <div className="invoice-overview-empty">No outstanding invoices found.</div>
          ) : null}

          {invoiceItems.length > 0 ? (
            <div className="invoice-overview-table-wrap">
              <table className="invoice-overview-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.map((item) => {
                    const statusText = formatInvoiceStatus(item);
                    const isOverdue = String(item?.v3_status || "").trim().toLowerCase() === "overdue";
                    return (
                      <tr key={item.invoice_id}>
                        <td>{item.invoice_number || item.invoice_id}</td>
                        <td>{item.client_name || "Unknown client"}</td>
                        <td>{formatInvoiceDueDate(item.due_date)}</td>
                        <td className={`invoice-status-cell ${isOverdue ? "invoice-status-overdue" : ""}`}>{statusText}</td>
                        <td>{formatInvoiceAmount(item.outstanding, item.currency_code)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {holidayError ? (
        <div className="holiday-feed-warning" role="status" aria-live="polite">
          {holidayError}
        </div>
      ) : null}

      <div className="calendar-card">
        {noVisibleEvents && (
          <div className="calendar-empty-banner" role="status" aria-live="polite">
            {hasSearch
              ? `No events match "${searchTerm.trim()}". Try a broader search.`
              : "No events to display yet. Click and drag in week/day view to add one."}
          </div>
        )}

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
          initialView={initialCalendarView || "dayGridMonth"}
          initialDate={initialCalendarDate || undefined}
          firstDay={1}
          timeZone="Europe/London"
          slotMinTime="00:00:00"
          scrollTime="06:00:00"
          scrollTimeReset={true}
          locale="en-gb"
          nowIndicator={false}
          views={{
            timeGridDay: {
              slotMinTime: "00:00:00",
              scrollTime: "06:00:00",
              scrollTimeReset: true,
            },
            timeGridWeek: {
              slotMinTime: "00:00:00",
              scrollTime: "06:00:00",
              scrollTimeReset: true,
            },
          }}
          customButtons={{
            searchBar: {
              text: "",
              click: () => {},
            },
            agendaToggle: {
              text: desktopAgendaOpen ? "Calendar" : "Agenda",
              click: () => {
                onToggleDesktopAgenda?.();
              },
            },
          }}
          datesSet={(arg) => {
            const nextViewType = arg.view?.type || "dayGridMonth";
            const prevViewType = previousViewTypeRef.current || "";
            const calendarApi = calendarRef.current?.getApi();

            if (
              !monthToWeekRedirectRef.current &&
              !monthDateClickToDayRef.current &&
              prevViewType === "dayGridMonth" &&
              (nextViewType === "timeGridWeek" || nextViewType === "timeGridDay") &&
              calendarApi
            ) {
              const nowIso = DateTime.now().setZone("Europe/London").toISODate();
              const apiDate = calendarApi.getDate?.();
              const apiIso = apiDate
                ? DateTime.fromJSDate(apiDate, { zone: "Europe/London" }).toISODate()
                : "";

              if (apiIso !== nowIso) {
                monthToWeekRedirectRef.current = true;
                calendarApi.changeView(nextViewType, nowIso);
                return;
              }
            }

            if (monthToWeekRedirectRef.current && (nextViewType === "timeGridWeek" || nextViewType === "timeGridDay")) {
              monthToWeekRedirectRef.current = false;
            }
            if (monthDateClickToDayRef.current && nextViewType === "timeGridDay") {
              monthDateClickToDayRef.current = false;
            }
            previousViewTypeRef.current = nextViewType;

            setActiveView({
              type: nextViewType,
              start: arg.start || null,
              end: arg.end || null,
              periodStart: arg.view?.currentStart || arg.start || null,
              periodEnd: arg.view?.currentEnd || arg.end || null,
            });
            onCalendarStateChange?.({
              view: nextViewType,
              date: calendarApi?.getDate?.() || arg.view?.currentStart || arg.start || null,
            });
            scheduleSidebarDecorators();
            const vt = nextViewType;
            if (vt === "timeGridDay" || vt === "timeGridWeek") {
              timeGridScrollPendingRef.current = true;
            } else {
              timeGridScrollPendingRef.current = false;
            }
          }}
          eventsSet={() => {
            if (!timeGridScrollPendingRef.current) return;
            const api = calendarRef.current?.getApi();
            if (!api) return;
            const vt = api.view?.type;
            if (vt !== "timeGridDay" && vt !== "timeGridWeek") return;
            timeGridScrollPendingRef.current = false;
            clearTimeout(timeGridScrollTimerRef.current);
            timeGridScrollTimerRef.current = setTimeout(() => {
              // If a post-update event focus was queued, skip the default scroll-to-6am
              // so we don't override the focused event's scroll position.
              if (suppressNextTimeGridScrollRef.current) {
                suppressNextTimeGridScrollRef.current = false;
                // Fallback: if eventDidMount didn't fire for the updated event, apply focus now.
                const pending = pendingUpdatedEventFocusRef.current;
                if (pending) {
                  const el = mountedEventElsRef.current.get(pending.eventId);
                  if (el && el.isConnected) {
                    pendingUpdatedEventFocusRef.current = null;
                    el.scrollIntoView({ block: "center", inline: "nearest" });
                    if (el._tippy && typeof el._tippy.hide === "function") {
                      el._tippy.hide();
                    }
                  }
                }
                return;
              }
              const scroller = document.querySelector(".fc-scroller-liquid-absolute");
              if (!scroller || !scroller.scrollHeight) return;
              const target = scroller.scrollHeight * (6 / 24);
              scroller.scrollTop = target;
              // Week view fires multiple FC internal scroll events; keep the
              // guard listener active for 2s to catch all of them, then remove.
              let guarding = true;
              const onScroll = () => {
                if (guarding) scroller.scrollTop = target;
              };
              scroller.addEventListener("scroll", onScroll);
              setTimeout(() => {
                guarding = false;
                scroller.removeEventListener("scroll", onScroll);
              }, 2000);
            }, 600);
          }}
          viewDidMount={() => {
            const btn = document.querySelector(".fc-searchBar-button");
            if (btn && !btn.querySelector("input")) {
              btn.style.padding = "0";
              btn.style.border = "none";
              btn.style.background = "transparent";

              const input = document.createElement("input");
              input.type = "text";
              input.placeholder = "Search...";
              input.className = "calendar-search-input";
              input.value = searchTerm;

              input.oninput = (e) => setSearchTerm(e.target.value);

              btn.appendChild(input);
            }

            const todayBtn = document.querySelector(".fc-today-button");
            if (todayBtn) {
              todayBtn.onclick = (event) => {
                event.preventDefault();
                event.stopPropagation();
                const calendarApi = calendarRef.current?.getApi();
                if (!calendarApi) return;

                const nowLondon = DateTime.now().setZone("Europe/London");
                const targetAnchor = nowLondon.startOf("month").minus({ months: 1 });
                const viewType = String(calendarApi.view?.type || "");
                const baseMonth =
                  viewType === "timeGridWeek"
                    ? nowLondon.startOf("week").startOf("month")
                    : nowLondon.startOf("month");
                const targetMonthIndex = targetAnchor.year * 12 + (targetAnchor.month - 1);
                const baseMonthIndex = baseMonth.year * 12 + (baseMonth.month - 1);

                setMiniMonthOffset(targetMonthIndex - baseMonthIndex);
                calendarApi.today();
              };
            }

            scheduleSidebarDecorators();
          }}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,agendaToggle searchBar",
          }}
          events={calendarEvents}
          eventDidMount={(info) => {
            info.el.setAttribute("data-event-id", String(info.event.id || ""));
            info.el.setAttribute("tabindex", "-1");
            mountedEventElsRef.current.set(String(info.event.id || ""), info.el);
            if (info.event.extendedProps?.isHoliday === true) return;

            focusPendingUpdatedEvent(info.event.id, info.el, info.view?.type || "");

          const formatTime = (date) => {
            if (!date) return "";
            const hours24 = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const suffix = hours24 >= 12 ? "pm" : "am";
            const hours12 = String(hours24 % 12 || 12).padStart(2, "0");
            return `${hours12}:${minutes}${suffix}`;
          };

          const getLiveEvent = () => {
            const calendarApi = calendarRef.current?.getApi();
            return calendarApi?.getEventById(info.event.id) || info.event;
          };

          const buildTooltipContent = () => {
            const liveEvent = getLiveEvent();
            const start = liveEvent.start;
            const end = liveEvent.end;

            let durationText = "";
            if (start && end) {
              const diffMs = end - start;
              const diffMins = Math.floor(diffMs / 60000);
              const hours = Math.floor(diffMins / 60);
              const mins = diffMins % 60;

              if (hours > 0 && mins > 0) durationText = `${hours}h ${mins}m`;
              else if (hours > 0) durationText = `${hours}h`;
              else durationText = `${mins}m`;
            }

            const title = liveEvent.title || "";
            const location = (liveEvent.extendedProps.location || "").trim();
            const invoice = (liveEvent.extendedProps.invoice_notes || "").trim();
            const internal = (liveEvent.extendedProps.internal_notes || "").trim();
            const timeRange = start && end
              ? `${formatTime(start)} - ${formatTime(end)}`
              : formatTime(start);

            return `
              <div style="margin-bottom:4px;"><strong>${title}</strong></div>

              <div style="opacity:0.7; margin-bottom:6px;">
                ${timeRange}${durationText ? ` (${durationText})` : ""}
              </div>

              ${location ? `<div style="margin-bottom:4px;">${location}</div>` : ""}

              ${invoice ? `<div style="opacity:0.8; margin-bottom:4px;">Invoice: ${invoice}</div>` : ""}

              ${internal ? `<div style="opacity:0.8;">Notes: ${internal}</div>` : ""}
            `;
          };

          const activeViewType =
            calendarRef.current?.getApi()?.view?.type || info.view?.type;
          const isDayView = activeViewType === "timeGridDay";

          // Destroy any pre-existing Tippy on this element before attaching a new one.
          // FullCalendar sometimes reuses DOM elements, leaving stale instances.
          if (info.el._tippy) {
            info.el._tippy.destroy();
          }

          tippy(info.el, {
            content: buildTooltipContent(),
            allowHTML: true,
            trigger: "mouseenter",
            placement: isDayView ? "top" : "right",
            popperOptions: {
              modifiers: [
                {
                  name: "flip",
                  options: {
                    fallbackPlacements:
                      isDayView
                        ? ["bottom", "top"]
                        : ["right", "left"],
                  },
                },
                {
                  name: "preventOverflow",
                  options: {
                    padding: 12,
                    boundary: "viewport",
                  },
                },
              ],
            },
            maxWidth: 320,
            animation: "shift-away",
            theme: "light-border",
            delay: [150, 0],
            onShow(instance) {
              instance.setContent(buildTooltipContent());

              const viewType = calendarRef.current?.getApi()?.view?.type;
              const inDayView = viewType === "timeGridDay";

              instance.setProps({
                placement: inDayView ? "top" : "right",
                offset: inDayView ? [0, 8] : [0, 14],
                popperOptions: {
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: inDayView
                          ? ["bottom", "top"]
                          : ["right", "left"],
                      },
                    },
                    {
                      name: "preventOverflow",
                      options: {
                        padding: 12,
                        boundary: "viewport",
                      },
                    },
                  ],
                },
              });
            },
          });
          }}
          eventWillUnmount={(info) => {
            mountedEventElsRef.current.delete(String(info.event.id || ""));
            if (info.el?._tippy) {
              info.el._tippy.destroy();
            }
          }}
          eventContent={(arg) => {
          const timeText = arg.timeText
            .replace(/:/g, ".")
            .replace(/\s?AM/i, "am")
            .replace(/\s?PM/i, "pm");
          const isSyncedToFreshbooks = Boolean(
            arg.event.extendedProps?.fb_time_entry_id
          );
          const hasReminder = arg.event.extendedProps?.reminder_enabled === true;
          const isMonthView = arg.view?.type === "dayGridMonth";
          const billingStatus = String(arg.event.extendedProps?.billing_status || "").trim().toLowerCase();
          const billingBadgeClass = billingStatus === "paid" ? " fb-sync-badge--paid" : billingStatus === "billed" ? " fb-sync-badge--billed" : "";
          const badgeClass = isMonthView ? "fb-sync-badge fb-sync-badge-month" : "fb-sync-badge";
          const badgesMarkup =
            isSyncedToFreshbooks || hasReminder
              ? `<span class="event-badges ${isMonthView ? "event-badges-month" : ""}">
                  ${isSyncedToFreshbooks ? `<span class="${badgeClass}${billingBadgeClass}" title="Synced to Freshbooks">FB</span>` : ""}
                  ${hasReminder ? `<span class="event-reminder-badge" title="Reminder enabled">⏰</span>` : ""}
                </span>`
              : "";

          if (isMonthView) {
            const monthCategoryColor = String(arg.event.extendedProps?.category_color || "").trim();
            const hasMonthCategoryColor = categoryColorRegex.test(monthCategoryColor);
            const useCategoryTint = hasMonthCategoryColor;
            const monthRowClass = hasMonthCategoryColor
              ? "fc-event-row fc-event-row-month fc-event-row-month-colored"
              : "fc-event-row fc-event-row-month";
            const monthRowStyle = useCategoryTint
              ? ` style="background-color:${hexToTint(monthCategoryColor, 0.5)};"`
              : "";

            return {
              html: `
                <div class="${monthRowClass}"${monthRowStyle}>
                  <div class="fc-event-title fc-event-title-month">${arg.event.title}</div>
                  ${badgesMarkup}
                </div>
              `,
            };
          }

          return {
            html: `
              <div class="fc-event-row">
                <div class="fc-event-time">${timeText}</div>
                ${badgesMarkup}
              </div>
              <div class="fc-event-title">${arg.event.title}</div>
            `,
          };
          }}
          dayHeaderContent={(arg) => {
          if (arg.view.type === "dayGridMonth") {
            return arg.date.toLocaleDateString("en-GB", {
              weekday: "short",
            });
          }
          return arg.text;
          }}
          slotLabelContent={(arg) => {
            const calendarApi = calendarRef.current?.getApi();
            if (!calendarApi) return { html: '<div class="tz-sidebar-slot"></div>' };
            const ukRowDateTime = getLondonRowDateTime(calendarApi, arg.text);
            const leftTime = formatDateTime12(ukRowDateTime.setZone(sidebarTimezones[0].zone));
            const rightTime = formatDateTime12(ukRowDateTime);

            const columns = `
              <div class="tz-sidebar-col">
                <span class="tz-sidebar-time">${leftTime}</span>
              </div>
              <div class="tz-sidebar-col">
                <span class="tz-sidebar-time">${rightTime}</span>
              </div>
            `;

            return {
              html: `<div class="tz-sidebar-slot">${columns}</div>`,
            };
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          selectable={true}
          selectMirror={true}
          selectLongPressDelay={0}
          selectAllow={(selectInfo) => {
            if (!selectInfo || !selectInfo.view) return true;
            return selectInfo.view.type !== "dayGridMonth";
          }}
          dateClick={(info) => {
            const calendarApi = info.view.calendar;

          if (info.view.type === "dayGridMonth") {
            monthDateClickToDayRef.current = true;
            calendarApi.changeView("timeGridDay", info.dateStr);
            return;
          }
          }}
          select={(info) => {
            if (info.view.type === "dayGridMonth") return;

          if (info.allDay) {
            openNewEventModal({ start: info.start, end: info.end, allDay: true });
          } else {
            const start = info.start;
            // Use a 1-hour minimum default for quick slot selection, while preserving longer dragged ranges.
            const rawEnd = info.end || new Date(start.getTime() + 60 * 60000);
            const selectedDurationMs = rawEnd.getTime() - start.getTime();
            const end =
              selectedDurationMs < 60 * 60000
                ? new Date(start.getTime() + 60 * 60000)
                : rawEnd;

            openNewEventModal({
              start,
              end,
              view: info.view,
            });
          }
          }}
          editable={true}
          eventStartEditable={true}
          eventDurationEditable={true}

          eventDragStart={() => { clearEventTooltips(); }}
          eventResizeStart={() => { clearEventTooltips(); }}

          eventDrop={async (info) => {
            clearEventTooltips();
            const event = info.event;
            if (event.extendedProps?.isHoliday === true) {
              info.revert();
              return;
            }

          // ── All-day dragged into time grid: revert + open modal as timed event ──
          if (info.oldEvent.allDay && !event.allDay) {
            info.revert();
            const pad = (n) => String(n).padStart(2, "0");
            const fmt = (d) =>
              `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            const droppedStart = event.start;
            const droppedEnd = new Date(droppedStart.getTime() + 60 * 60000);
            const recurrence = getRecurrenceMetadata(
              event.extendedProps,
              info.oldEvent.extendedProps
            );
            setModalData({
              id: event.id,
              title: event.title || "",
              all_day: false,
              start: fmt(droppedStart),
              end: fmt(droppedEnd),
              repeat_rule: recurrence.repeat_rule || "none",
              repeat_until: recurrence.repeat_until || "",
              is_lesson: event.extendedProps?.is_lesson ?? false,
              no_show: event.extendedProps?.no_show ?? false,
              fb_time_entry_id: event.extendedProps?.fb_time_entry_id ?? "",
              project_id: event.extendedProps?.project_id ?? null,
              project_name: event.extendedProps?.project_name ?? "",
              invoice_notes: event.extendedProps?.invoice_notes ?? "",
              internal_notes: event.extendedProps?.internal_notes ?? "",
              location: event.extendedProps?.location ?? "",
              category_id: event.extendedProps?.category_id ?? "",
              category_name: event.extendedProps?.category_name ?? "",
              category_color: event.extendedProps?.category_color ?? "",
              reminder_enabled: event.extendedProps?.reminder_enabled ?? false,
              reminder_config: event.extendedProps?.reminder_config ?? "",
              series_id: recurrence.series_id || "",
            });
            setIsEdit(true);
            setModalOpen(true);
            return;
          }

          const seriesId = getRecurrenceMetadata(
            event.extendedProps,
            info.oldEvent.extendedProps
          ).series_id;
          const askDropScope = async () => {
            if (!seriesId) return "this";
            return requestRecurringScope("move");
          };

          const dropScope = await askDropScope();
          if (dropScope === null) {
            info.revert();
            return;
          }

          // ── All-day moved within all-day row ──
          if (event.allDay) {
            const pad = (n) => String(n).padStart(2, "0");
            const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const recurrence = getRecurrenceMetadata(
              event.extendedProps,
              info.oldEvent.extendedProps
            );
            const singlePayload = {
              title: event.title,
              start: dateStr(event.start),
              end: event.end ? dateStr(event.end) : dateStr(event.start),
              extendedProps: {
                project_id: event.extendedProps.project_id ? String(event.extendedProps.project_id) : "",
                project_name: event.extendedProps.project_name || "",
                is_lesson: event.extendedProps.is_lesson === true,
                no_show: event.extendedProps.no_show === true,
                fb_time_entry_id: event.extendedProps.fb_time_entry_id || "",
                invoice_notes: event.extendedProps.invoice_notes || "",
                internal_notes: event.extendedProps.internal_notes || "",
                location: event.extendedProps.location || "",
                reminder_enabled: event.extendedProps.reminder_enabled === true,
                reminder_config: event.extendedProps.reminder_config || "",
                series_id: recurrence.series_id || seriesId,
                repeat_rule: recurrence.repeat_rule || "",
                repeat_until: recurrence.repeat_until || "",
              },
            };

            if (dropScope === "this" || !seriesId) {
              await update(event.id, singlePayload);
              return;
            }

            const originalStart = new Date(info.oldEvent.start);
            const movedStart = new Date(event.start);
            const shiftMs = movedStart.getTime() - originalStart.getTime();
            const anchorDate = originalStart.toISOString().slice(0, 10);

            info.revert();
            const targets = events.filter((ev) =>
              ev.extendedProps?.series_id === seriesId &&
              String(ev.start || "").slice(0, 10) >= anchorDate
            );

            for (const target of targets) {
              const targetStart = new Date(target.start);
              const targetEnd = new Date(target.end);
              targetStart.setTime(targetStart.getTime() + shiftMs);
              targetEnd.setTime(targetEnd.getTime() + shiftMs);

              const payload = {
                title: target.title,
                start: dateStr(targetStart),
                end: dateStr(targetEnd),
                extendedProps: {
                  project_id: target.extendedProps?.project_id ? String(target.extendedProps.project_id) : "",
                  project_name: target.extendedProps?.project_name || "",
                  is_lesson: target.extendedProps?.is_lesson === true,
                  no_show: target.extendedProps?.no_show === true,
                  fb_time_entry_id: target.extendedProps?.fb_time_entry_id || "",
                  invoice_notes: target.extendedProps?.invoice_notes || "",
                  internal_notes: target.extendedProps?.internal_notes || "",
                  location: target.extendedProps?.location || "",
                  reminder_enabled: target.extendedProps?.reminder_enabled === true,
                  reminder_config: target.extendedProps?.reminder_config || "",
                  ...getRecurrenceMetadata(target.extendedProps, info.oldEvent.extendedProps),
                },
              };

              await updateSilently(target.id, payload);
            }

            await reload();
            return;
          }

          // ── Normal timed event drop ──
          const start = roundTo30(event.start).toISOString();
          const end = event.end
            ? roundTo30(event.end).toISOString()
            : new Date(roundTo30(event.start).getTime() + 60 * 60000).toISOString();

          const updated = {
            title: event.title,
            start,
            end,
            extendedProps: {
              project_id: event.extendedProps.project_id
                ? String(event.extendedProps.project_id)
                : "",
              project_name: event.extendedProps.project_name || "",
              is_lesson: event.extendedProps.is_lesson === true,
              no_show: event.extendedProps.no_show === true,
              fb_time_entry_id: event.extendedProps.fb_time_entry_id || "",
              invoice_notes: event.extendedProps.invoice_notes || "",
              internal_notes: event.extendedProps.internal_notes || "",
              location: event.extendedProps.location || "",
              reminder_enabled: event.extendedProps.reminder_enabled === true,
              reminder_config: event.extendedProps.reminder_config || "",
              ...getRecurrenceMetadata(event.extendedProps, info.oldEvent.extendedProps),
            },
          };

            if (dropScope === "this" || !seriesId) {
              await update(event.id, updated);
            } else {
              const oldStart = new Date(info.oldEvent.start);
              const newStart = roundTo30(event.start);
              const shiftMs = newStart.getTime() - oldStart.getTime();
              const anchorDate = oldStart.toISOString().slice(0, 10);

              info.revert();
              const targets = events.filter((ev) =>
                ev.extendedProps?.series_id === seriesId &&
                String(ev.start || "").slice(0, 10) >= anchorDate
              );

              for (const target of targets) {
                const targetStart = roundTo30(new Date(target.start));
                const targetEnd = target.end
                  ? roundTo30(new Date(target.end))
                  : new Date(targetStart.getTime() + 60 * 60000);

                targetStart.setTime(targetStart.getTime() + shiftMs);
                targetEnd.setTime(targetEnd.getTime() + shiftMs);

                const targetPayload = {
                  title: target.title,
                  start: targetStart.toISOString(),
                  end: targetEnd.toISOString(),
                  extendedProps: {
                    project_id: target.extendedProps?.project_id ? String(target.extendedProps.project_id) : "",
                    project_name: target.extendedProps?.project_name || "",
                    is_lesson: target.extendedProps?.is_lesson === true,
                    no_show: target.extendedProps?.no_show === true,
                    fb_time_entry_id: target.extendedProps?.fb_time_entry_id || "",
                    invoice_notes: target.extendedProps?.invoice_notes || "",
                    internal_notes: target.extendedProps?.internal_notes || "",
                    location: target.extendedProps?.location || "",
                    reminder_enabled: target.extendedProps?.reminder_enabled === true,
                    reminder_config: target.extendedProps?.reminder_config || "",
                    ...getRecurrenceMetadata(target.extendedProps, info.oldEvent.extendedProps),
                  },
                };
                await updateSilently(target.id, targetPayload);

                const targetFbId = target.extendedProps?.fb_time_entry_id;
                if (target.extendedProps?.is_lesson && targetFbId) {
                  const targetNotes = target.extendedProps?.no_show
                    ? `[No Show] ${target.extendedProps?.invoice_notes || ""}`.trim()
                    : target.extendedProps?.invoice_notes || "";
                  try {
                    await updateTimeEntry(targetFbId, {
                      projectId: target.extendedProps?.project_id,
                      eventTitle: target.title,
                      eventStart: targetPayload.start,
                      eventEnd: targetPayload.end,
                      notes: targetNotes,
                    });
                  } catch (err) {
                    window.alert(`Calendar updated but Freshbooks sync failed:\n${err.message}`);
                  }
                }
              }

              await reload();
            }

            const fbId = event.extendedProps.fb_time_entry_id;
            if (event.extendedProps.is_lesson && fbId) {
              const notes = event.extendedProps.no_show
                ? `[No Show] ${event.extendedProps.invoice_notes || ""}`.trim()
                : event.extendedProps.invoice_notes || "";
              try {
                await updateTimeEntry(fbId, { projectId: event.extendedProps.project_id, eventTitle: event.title, eventStart: start, eventEnd: end, notes });
              } catch (err) {
                window.alert(`Calendar updated but Freshbooks sync failed:\n${err.message}`);
              }
            }
          }}

          eventResize={async (info) => {
            clearEventTooltips();
            const event = info.event;
            if (event.extendedProps?.isHoliday === true) {
              info.revert();
              return;
            }
            const seriesId = getRecurrenceMetadata(
              event.extendedProps,
              info.oldEvent.extendedProps
            ).series_id;
            const askResizeScope = async () => {
              if (!seriesId) return "this";
              return requestRecurringScope("resize");
            };
            const resizeScope = await askResizeScope();
            if (resizeScope === null) {
              info.revert();
              return;
            }

          // ── All-day event resized in the all-day row ──
          if (event.allDay) {
            const pad = (n) => String(n).padStart(2, "0");
            const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const recurrence = getRecurrenceMetadata(
              event.extendedProps,
              info.oldEvent.extendedProps
            );
            const resizedStartDate = parseDateOnly(event.startStr || event.start);
            const resizedEndDate = parseDateOnly(event.endStr || event.end || event.startStr || event.start);
            const singlePayload = {
              title: event.title,
              start: resizedStartDate ? dateStr(resizedStartDate) : dateStr(event.start),
              end: resizedEndDate
                ? dateStr(resizedEndDate)
                : (event.end ? dateStr(event.end) : dateStr(event.start)),
              extendedProps: {
                project_id: event.extendedProps.project_id ? String(event.extendedProps.project_id) : "",
                project_name: event.extendedProps.project_name || "",
                is_lesson: event.extendedProps.is_lesson === true,
                no_show: event.extendedProps.no_show === true,
                fb_time_entry_id: event.extendedProps.fb_time_entry_id || "",
                invoice_notes: event.extendedProps.invoice_notes || "",
                internal_notes: event.extendedProps.internal_notes || "",
                location: event.extendedProps.location || "",
                reminder_enabled: event.extendedProps.reminder_enabled === true,
                reminder_config: event.extendedProps.reminder_config || "",
                series_id: recurrence.series_id || seriesId,
                repeat_rule: recurrence.repeat_rule || "",
                repeat_until: recurrence.repeat_until || "",
              },
            };

            if (resizeScope === "this" || !seriesId) {
              await update(event.id, singlePayload);
              return;
            }

            const oldEnd = parseDateOnly(info.oldEvent.endStr || info.oldEvent.end || info.oldEvent.startStr || info.oldEvent.start);
            const newEnd = parseDateOnly(event.endStr || event.end || event.startStr || event.start);
            if (!oldEnd || !newEnd) {
              info.revert();
              return;
            }

            const deltaDays = Math.round(
              (newEnd.getTime() - oldEnd.getTime()) / (24 * 60 * 60 * 1000)
            );
            const anchorDate = String(info.oldEvent.startStr || info.oldEvent.start || "").slice(0, 10);

            info.revert();
            const targets = events.filter((ev) =>
              ev.extendedProps?.series_id === seriesId &&
              String(ev.start || "").slice(0, 10) >= anchorDate
            );

            for (const target of targets) {
              const targetStart = parseDateOnly(target.start);
              const targetCurrentEnd = parseDateOnly(target.end || target.start);
              if (!targetStart || !targetCurrentEnd) continue;

              const shiftedEnd = addDays(targetCurrentEnd, deltaDays);

              const targetPayload = {
                title: target.title,
                start: dateStr(targetStart),
                end: dateStr(shiftedEnd),
                extendedProps: {
                  project_id: target.extendedProps?.project_id ? String(target.extendedProps.project_id) : "",
                  project_name: target.extendedProps?.project_name || "",
                  is_lesson: target.extendedProps?.is_lesson === true,
                  no_show: target.extendedProps?.no_show === true,
                  fb_time_entry_id: target.extendedProps?.fb_time_entry_id || "",
                  invoice_notes: target.extendedProps?.invoice_notes || "",
                  internal_notes: target.extendedProps?.internal_notes || "",
                  location: target.extendedProps?.location || "",
                  reminder_enabled: target.extendedProps?.reminder_enabled === true,
                  reminder_config: target.extendedProps?.reminder_config || "",
                  ...getRecurrenceMetadata(target.extendedProps, info.oldEvent.extendedProps),
                },
              };

              await updateSilently(target.id, targetPayload);

              const targetFbId = target.extendedProps?.fb_time_entry_id;
              if (target.extendedProps?.is_lesson && targetFbId) {
                const targetNotes = target.extendedProps?.no_show
                  ? `[No Show] ${target.extendedProps?.invoice_notes || ""}`.trim()
                  : target.extendedProps?.invoice_notes || "";
                try {
                  await updateTimeEntry(targetFbId, {
                    projectId: target.extendedProps?.project_id,
                    eventTitle: target.title,
                    eventStart: targetPayload.start,
                    eventEnd: targetPayload.end,
                    notes: targetNotes,
                  });
                } catch (err) {
                  window.alert(`Calendar updated but Freshbooks sync failed:\n${err.message}`);
                }
              }
            }

            await reload();
            return;
          }

          // ── Normal timed event resize ──
          const start = roundTo30(event.start).toISOString();
          const end = event.end
            ? roundTo30(event.end).toISOString()
            : new Date(roundTo30(event.start).getTime() + 60 * 60000).toISOString();

          const updated = {
            title: event.title,
            start,
            end,
            extendedProps: {
              project_id: event.extendedProps.project_id
                ? String(event.extendedProps.project_id)
                : "",
              project_name: event.extendedProps.project_name || "",
              is_lesson: event.extendedProps.is_lesson === true,
              no_show: event.extendedProps.no_show === true,
              fb_time_entry_id: event.extendedProps.fb_time_entry_id || "",
              invoice_notes: event.extendedProps.invoice_notes || "",
              internal_notes: event.extendedProps.internal_notes || "",
              location: event.extendedProps.location || "",
              reminder_enabled: event.extendedProps.reminder_enabled === true,
              reminder_config: event.extendedProps.reminder_config || "",
              ...getRecurrenceMetadata(event.extendedProps, info.oldEvent.extendedProps),
            },
          };

            if (resizeScope === "this" || !seriesId) {
              await update(event.id, updated);
            } else {
              const oldEnd = info.oldEvent.end ? new Date(info.oldEvent.end) : new Date(info.oldEvent.start);
              const newEnd = event.end ? roundTo30(event.end) : new Date(roundTo30(event.start).getTime() + 60 * 60000);
              const deltaMs = newEnd.getTime() - oldEnd.getTime();
              const anchorDate = String(info.oldEvent.startStr || info.oldEvent.start || "").slice(0, 10);

              info.revert();
              const targets = events.filter((ev) =>
                ev.extendedProps?.series_id === seriesId &&
                String(ev.start || "").slice(0, 10) >= anchorDate
              );

              for (const target of targets) {
                const targetStart = roundTo30(new Date(target.start));
                const targetEnd = target.end
                  ? roundTo30(new Date(target.end))
                  : new Date(targetStart.getTime() + 60 * 60000);
                targetEnd.setTime(targetEnd.getTime() + deltaMs);

                const targetPayload = {
                  title: target.title,
                  start: targetStart.toISOString(),
                  end: targetEnd.toISOString(),
                  extendedProps: {
                    project_id: target.extendedProps?.project_id ? String(target.extendedProps.project_id) : "",
                    project_name: target.extendedProps?.project_name || "",
                    is_lesson: target.extendedProps?.is_lesson === true,
                    no_show: target.extendedProps?.no_show === true,
                    fb_time_entry_id: target.extendedProps?.fb_time_entry_id || "",
                    invoice_notes: target.extendedProps?.invoice_notes || "",
                    internal_notes: target.extendedProps?.internal_notes || "",
                    location: target.extendedProps?.location || "",
                    reminder_enabled: target.extendedProps?.reminder_enabled === true,
                    reminder_config: target.extendedProps?.reminder_config || "",
                    ...getRecurrenceMetadata(target.extendedProps, info.oldEvent.extendedProps),
                  },
                };
                await updateSilently(target.id, targetPayload);

                const targetFbId = target.extendedProps?.fb_time_entry_id;
                if (target.extendedProps?.is_lesson && targetFbId) {
                  const targetNotes = target.extendedProps?.no_show
                    ? `[No Show] ${target.extendedProps?.invoice_notes || ""}`.trim()
                    : target.extendedProps?.invoice_notes || "";
                  try {
                    await updateTimeEntry(targetFbId, {
                      projectId: target.extendedProps?.project_id,
                      eventTitle: target.title,
                      eventStart: targetPayload.start,
                      eventEnd: targetPayload.end,
                      notes: targetNotes,
                    });
                  } catch (err) {
                    window.alert(`Calendar updated but Freshbooks sync failed:\n${err.message}`);
                  }
                }
              }

              await reload();
            }

            const fbId = event.extendedProps.fb_time_entry_id;
            if (event.extendedProps.is_lesson && fbId) {
              const notes = event.extendedProps.no_show
                ? `[No Show] ${event.extendedProps.invoice_notes || ""}`.trim()
                : event.extendedProps.invoice_notes || "";
              try {
                await updateTimeEntry(fbId, { projectId: event.extendedProps.project_id, eventTitle: event.title, eventStart: start, eventEnd: end, notes });
              } catch (err) {
                window.alert(`Calendar updated but Freshbooks sync failed:\n${err.message}`);
              }
            }
          }}

          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            ...(timeFormat === "24h" ? { hour12: false } : { meridiem: "short", hour12: true }),
          }}
          eventClick={handleEventClick}
          eventClassNames={(arg) => {
            const classNames = [];
            if (arg.event.extendedProps.is_lesson) {
              classNames.push("lesson-event");
            }
            if (categoryColorRegex.test(String(arg.event.extendedProps?.category_color || "").trim())) {
              classNames.push("category-event");
            }
            if (arg.event.extendedProps.fb_time_entry_id) {
              classNames.push("fb-synced-event");
            }
            return classNames;
          }}
          height="85vh"
        />
      </div>

      <EventModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalData({});
        }}
        data={modalData}
        setData={setModalData}
        isEdit={isEdit}
        onSave={isEdit ? handleUpdate : handleSave}
        onDelete={isEdit ? handleDelete : undefined}
        onDeleteFollowing={isEdit ? handleModalDeleteFollowing : undefined}
        onDeleteAll={isEdit ? handleModalDeleteAll : undefined}
        isSaving={isSaving}
        categories={categories}
        timeFormat={timeFormat}
        onOpenCategoryManager={() => setCategoryModalOpen(true)}
      />

      <CategoryManagerModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        onCreate={handleCreateCategory}
        onUpdate={handleUpdateCategory}
        onDelete={handleDeleteCategory}
        isSaving={categorySaving}
        error={categoryError}
      />

      {seriesModalInfo && (
        <SeriesActionModal
          eventTitle={seriesModalInfo.event.title}
          onEditThis={handleSeriesEditThis}
          onDeleteThis={handleSeriesDeleteThis}
          onDeleteFollowing={handleSeriesDeleteFollowing}
          onDeleteAll={handleSeriesDeleteAll}
          onClose={() => setSeriesModalInfo(null)}
        />
      )}

      {recurringScopeModal && (
        <RecurringScopeModal
          actionLabel={recurringScopeModal.actionLabel}
          onThisOnly={() => resolveRecurringScope("this")}
          onThisAndFollowing={() => resolveRecurringScope("following")}
          onCancel={() => resolveRecurringScope(null)}
        />
      )}

      <NotificationSettingsModal
        isOpen={notificationModalOpen}
        settings={notificationSettings}
        loading={notificationLoading}
        saving={notificationSaving}
        error={notificationError}
        saveMessage={notificationSaveMessage}
        permission={browserPermission}
        showAdminTools={isAdminUser}
        adminToolsOpen={dispatcherStatusOpen}
        dispatcherLoading={dispatcherStatusLoading}
        dispatcherError={dispatcherStatusError}
        dispatcherStatus={dispatcherStatus}
        formatStatusTimestamp={formatStatusTimestamp}
        onToggleAdminTools={handleAdminToolsToggle}
        onRefreshDispatcherStatus={refreshDispatcherStatus}
        onClose={() => setNotificationModalOpen(false)}
        onChange={handleNotificationSettingsChange}
        onSave={handleNotificationSettingsSave}
        onRequestPermission={handleNotificationPermissionRequest}
        onTestNotification={handleTestNotification}
        onTestWhatsApp={handleTestWhatsApp}
        whatsappTestSending={whatsappTestSending}
        whatsappHasUnsavedChanges={hasUnsavedWhatsAppSettings}
        browserTestMessage={browserTestMessage}
        whatsappTestMessage={whatsappTestMessage}
      />
    </div>
  );
}

export default Calendar;


        